import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { debounce } from 'lodash'; // or implement your own debounce
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import { Icon } from '@lomarengas/design-system';
import { PayloadAction } from '@reduxjs/toolkit';
import { formatUtcDateTimeToFinnish } from '../../common/utils/dateUtils';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';
import { AccommodationImageType } from './AccommodationImages';
import ImagesView from './Components/ImagesView';
import AddOnList from './Components/AddOnList';
import CopyAccommodationModal from './Components/CopyAccommodationModal';

import {
  addStatusToast,
  Loader,
  PaginationComponent,
  SectionHeader,
  VerticalTabPane,
  VerticalTabs,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccommodationGroupQueryFilters,
  getAllAccommodationGroupsAsync,
  selectAccommodationGroups,
} from './accommodationGroupsSlice';

import {
  AccountsFilter,
  addAddressDataAsync,
  AddressResponse,
  getAccountRolesFromDbAsync,
  getAllAccountsFromDbAsync,
  getAllRolesAsync,
  getIdentityAddressDetailsAsync,
  getOwnerMaintainerAccountsAsync,
  selectAddressDetailsFromDb,
  selectAddressDetailsLoadingStatus,
  selectAllAccountsFromDb,
  selectAllRoles,
  selectOwnerMaintainerAccounts,
  selectOwnerMaintainerAccountsLoadingStatus,
  selectUserRoles,
  selectUserRolesLoadingStatus,
  UpdateAddressData,
  updateAddressDataAsync,
} from '../Users/identityUsersSlice';

import {
  moveMaintainerBookingsToNewMaintainerAsync,
  moveOwnerBookingsToNewOwnerAsync,
} from '../bookings/bookingsSlice';

import {
  AccommodationAccount,
  AccommodationAccountsUpdate,
  AccommodationAddonItem,
  AccommodationAddOns,
  AccommodationAddOnsUpdate,
  AccommodationAttributeItem,
  AccommodationAttributes,
  AccommodationAttributesUpdate,
  AccommodationDetailsBasic,
  AccommodationDetailsPublicNotes,
  AccommodationDetailsTranslations,
  AccommodationDetailsUpdateBasic,
  AccommodationFilteredHistoryLogsRequest,
  AccommodationGeoCode,
  AccommodationImage,
  AccommodationInternalNote,
  AccommodationNotes,
  AccommodationNotesUpdate,
  AccommodationOptionValueItem,
  AccommodationUpdateImages,
  AccommodationUploadImages,
  AccountListItem,
  AddBuilding,
  DeleteAccommodationImage,
  deleteAccommodationImageAsync,
  getAccommodationAccountsAsync,
  getAccommodationAddOnsAccountsAsync,
  getAccommodationAddOnsAsync,
  getAccommodationAttributesAsync,
  getAccommodationDetailsBasicAsync,
  getAccommodationImagesAsync,
  getAccommodationLogsAsync,
  getAccommodationNotesAsync,
  getAccommodationOwnerValidationsAsync,
  RemoveBuilding,
  RewriteDescriptionCommand,
  TranslatePropertiesCommand,
  selectAccommodationAccounts,
  selectAccommodationAccountsLoadingStatus,
  selectAccommodationAddOns,
  selectAccommodationAddOnsLoadingStatus,
  selectAccommodationDetailsBasic,
  selectAccommodationDetailsLoadingStatus,
  selectAccommodationImages,
  selectAccommodationImagesLoadingStatus,
  selectAccommodationLogs,
  selectAccommodationLogsLoadingStatus,
  selectAccommodationNotes,
  selectAccommodationsAttributes,
  fixAccommodationImageIndexesAsync,
  tranferImagesAccommodationAsync,
  updateAccommodatioBasicAsync,
  updateAccommodationAccountsAsync,
  updateAccommodationImageIndexesAsync,
  updateAccommodationNotesAsync,
  updateAccommodationsAddOnsAsync,
  updateAccommodationsAttributesAsync,
  uploadFilesToAzureAsync,
  rewriteDescriptionAsync,
  translateDescriptionAsync,
  getAccommodationCapacitiesAsync,
  setAccommodationCapacitiesAsync,
  selectAccommodationCapacities,
  SetCapacitiesCommand,
} from './accommodationsSlice';

import {
  AccommodationAttributeListItem,
  AccommodationAttributeOptionItem,
  AccommodationAttributeQueryFilters,
  addBuildingAsync,
  AttributeRoomAreaData,
  AttributeRoomData,
  BuildingData,
  BuildingRoomsQueryFilters,
  BuildingUpdateData,
  getAccommodationAttributeOptionsAsync,
  getAllAccommodationAttributesAsync,
  getAllAccommodationBuildingsAsync,
  getAllBedsAsync,
  getAllBuildingRoomAreasAsync,
  getAllBuildingRoomsAsync,
  getAllBuildingsAsync,
  getAllFloorsAsync,
  getAllRoomAreasAsync,
  getAllRoomsAsync,
  removeBuildingAsync,
  RoomsAndAreasFilter,
  selectAccommodationAttributeOptions,
  selectAccommodationAttributes,
  selectAccommodationAttributesLoadingStatus,
  selectAccommodationBuildings,
  selectAccommodationBuildingsLoadingStatus,
  selectAllBeds,
  selectAllBuildings,
  selectAllFloors,
  selectAttributeRoomAreas,
  selectAttributeRooms,
  selectRoomAreas,
  selectRooms,
  updateBuildingAsync,
  updateBuildingRoomAreaAsync,
  updateBuildingRoomAsync,
  updateBuildingRoomBedAsync,
} from './accommodationAttributesSlice';

import {
  AccountingsQueryFilters,
  getAllPurchaseVatsAsync,
} from '../accountings/accountingSlice';

import {
  AddOnsQueryFilters,
  getAllAddOnsAsync,
  selectAddOns,
} from '../addons/addonSlice';

import { LOADING_STATUS, LOCALES, OWNER_ROLE } from '../../common/constants';
import {
  getCountriesAsync,
  selectCountryItems,
} from '../countries/countriesSlice';
import {
  getMunicipalitiesAsync,
  selectMunicipalityItems,
} from '../municipalities/municipalitiesSlice';
import {
  getSkiResortsAsync,
  selectSkiResortItems,
} from '../skiResorts/skiResortsSlice';
import { getRegionsAsync, selectRegionItems } from '../regions/regionsSlice';
import { getPlacesAsync, selectPlaceItems } from '../places/placesSlice';
import AccommodationInputNumber from './Components/AccommodationInputNumber';
import AccommodationInputSelect from './Components/AccommodationInputSelect';
import AccommodationInputText from './Components/AccommodationInputText';
// import { NumericMaxEdit } from './Components/NumericMaxModal';
// import { NumericMinEdit } from './Components/NumericMinModal';
// import { BooleanEdit } from './Components/BooleanModal';
// import { FreeTextEdit } from './Components/FreeTextModal';
// import { MultiSelectEdit } from './Components/MultiSelectModal';
// import { AddOnEdit } from './Components/AddOnModal';
// import { AddOnAddNewModal } from './Components/AddOnAddNewModal';
// import { BuildingModal } from './Components/BuildingModal';
// import { AddNewBuildingModal } from './Components/AddNewBuildingModal';
// import Attributes from './Components/Attributes';
// import CalendarView from './Components/CalendarView';
// import AccommodationForm from './Components/AccommodationForm';
// import AccommodationDescriptions from './Components/AccommodationDescriptions';
// import AttributesAddAll from './Components/AttributesAddAll';
import seasonRates from '../seasonRates/SeasonRates';
import { getSeasonRatesByAccommodationAsync } from './CapacityCalendar/CapacityCalendarSlice';
import { N8Nresponse } from './Components/AccommodationDescriptions';
import { fetchFeedbacks } from '../../common/api/feedBackService';

const AccommodationDescriptions = lazy(
  () => import('./Components/AccommodationDescriptions'),
);
const AttributesAddAll = lazy(() => import('./Components/AttributesAddAll'));
const AccommodationForm = lazy(() => import('./Components/AccommodationForm'));
const CalendarView = lazy(() => import('./Components/CalendarView'));
const Attributes = lazy(() => import('./Components/Attributes'));
const AddNewBuildingModal = lazy(() =>
  import('./Components/AddNewBuildingModal').then(module => ({
    default: module.AddNewBuildingModal,
  })),
);

const BuildingModal = lazy(() =>
  import('./Components/BuildingModal').then(module => ({
    default: module.BuildingModal,
  })),
);

const AddOnAddNewModal = lazy(() =>
  import('./Components/AddOnAddNewModal').then(module => ({
    default: module.AddOnAddNewModal,
  })),
);

const AddOnEdit = lazy(() =>
  import('./Components/AddOnModal').then(module => ({
    default: module.AddOnEdit,
  })),
);

const MultiSelectEdit = lazy(() =>
  import('./Components/MultiSelectModal').then(module => ({
    default: module.MultiSelectEdit,
  })),
);

const NumericPlainEdit = lazy(() =>
  import('./Components/NumericPlainModal').then(module => ({
    default: module.NumericPlainEdit,
  })),
);

const NumericMaxEdit = lazy(() =>
  import('./Components/NumericMaxModal').then(module => ({
    default: module.NumericMaxEdit,
  })),
);

const NumericMinEdit = lazy(() =>
  import('./Components/NumericMinModal').then(module => ({
    default: module.NumericMinEdit,
  })),
);

const BooleanEdit = lazy(() =>
  import('./Components/BooleanModal').then(module => ({
    default: module.BooleanEdit,
  })),
);

const FreeTextEdit = lazy(() =>
  import('./Components/FreeTextModal').then(module => ({
    default: module.FreeTextEdit,
  })),
);

const IcalIntegrations = lazy(() => import('./Components/IcalIntegrations'));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export interface TravelAgencyDomain {
  id: string;
  partnerName: string;
}

function AccommodationDetailsView() {
  // Clean up images
  const params = useParams();
  const query = useQuery();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const initialAccommodationDetailsBasic = {} as AccommodationDetailsBasic;
  const initialAccommodationNotes = {
    internalNotes: [],
    publicNotes: [],
  } as AccommodationNotes;
  const initialAccommodationAttributes = {
    accommodationAttributes: [],
  } as AccommodationAttributes;
  const initialAccommodationAddOns = { addOns: [] } as AccommodationAddOns;

  const accommodationGroups = useAppSelector(selectAccommodationGroups);
  const accommodationDetailsBasic = useAppSelector(
    selectAccommodationDetailsBasic,
  );
  const buildings = useAppSelector(selectAccommodationBuildings);
  const buildingsLoadingStatus = useAppSelector(
    selectAccommodationBuildingsLoadingStatus,
  );
  const [seasonRatesFetched, setSeasonRatesFetched] = useState(null);
  const [seasonsLoading, setSeasonsLoading] = useState(LOADING_STATUS.IDLE);

  function getSeasonRates(AcId) {
    setSeasonsLoading(LOADING_STATUS.LOADING);
    dispatch(getSeasonRatesByAccommodationAsync(AcId))
      .then(result => {
        if (getSeasonRatesByAccommodationAsync.fulfilled.match(result)) {
          setSeasonsLoading(LOADING_STATUS.IDLE);
          const rates = result.payload;
          setSeasonRatesFetched(rates);
        } else {
          setSeasonsLoading(LOADING_STATUS.FAILED);
          console.error('Failed to fetch rates info:', result.payload);
        }
      })
      .catch(error => {
        setSeasonsLoading(LOADING_STATUS.FAILED);
        console.error('Error:', error);
      });
  }
  useEffect(() => {}, [seasonRates]);
  const allBuildings = useAppSelector(selectAllBuildings);
  const allFloors = useAppSelector(selectAllFloors);
  const accommodationLogs = useAppSelector(selectAccommodationLogs);
  const accommodationImages = useAppSelector(selectAccommodationImages);
  const [accommodationBasicTemp, setAccommodationBasic] = useState(
    initialAccommodationDetailsBasic,
  );

  const [accommodationLatitude, setAccommodationLatitude] =
    useState<number>(null);
  const [showAttributesModal, setshowAttributesModal] =
    useState<boolean>(false);
  const [accommodationLongitude, setAccommodationLongitude] =
    useState<number>(null);
  const originalAccommodationAccountList = useAppSelector(
    selectAccommodationAccounts,
  );
  const originalAccommodationAccountListLoading = useAppSelector(
    selectAccommodationAccountsLoadingStatus,
  );
  const accommodationImagesLoadingStatus = useAppSelector(
    selectAccommodationImagesLoadingStatus,
  );

  const travelAgencyAccounts = useAppSelector(selectAllAccountsFromDb);
  const accommodationAddOnList = useAppSelector(selectAccommodationAddOns);
  const accommodationNotes = useAppSelector(selectAccommodationNotes);
  const accommodationAttributes = useAppSelector(selectAccommodationAttributes);
  const buildingRooms = useAppSelector(selectAttributeRooms);
  const buildingRoomAreas = useAppSelector(selectAttributeRoomAreas);
  const allRooms = useAppSelector(selectRooms);
  const allBeds = useAppSelector(selectAllBeds);
  const allRoomAreas = useAppSelector(selectRoomAreas);
  const countries = useAppSelector(selectCountryItems);
  const regions = useAppSelector(selectRegionItems);
  const municipalities = useAppSelector(selectMunicipalityItems);
  const skiResorts = useAppSelector(selectSkiResortItems);
  const places = useAppSelector(selectPlaceItems);
  const addedAccountRoles = useAppSelector(selectUserRoles);
  const allRoles = useAppSelector(selectAllRoles);
  const accountRoles = useAppSelector(selectUserRoles);
  const accountRolesLoading = useAppSelector(selectUserRolesLoadingStatus);
  const accommodationAttributesLoadingStatus = useAppSelector(
    selectAccommodationAttributesLoadingStatus,
  );
  const accommodationAddOnsLoadingStatus = useAppSelector(
    selectAccommodationAddOnsLoadingStatus,
  );
  const newAttributeOptions = useAppSelector(
    selectAccommodationAttributeOptions,
  );
  const addOnsList = useAppSelector(selectAddOns);
  const addressDetails = useAppSelector(selectAddressDetailsFromDb);
  const addressLoadingStatus = useAppSelector(
    selectAddressDetailsLoadingStatus,
  );
  const [isAddAccountModalVisible, setIsAddAccountModalVisible] =
    useState<boolean>(false);
  const [addressDetailsTemp, setAddress] = useState(addressDetails);
  const [showFreeText, setShowFreeText] = useState(false);
  const [showNumericMin, setShowNumericMin] = useState(false);
  const [showMultiSelect, setShowMultiSelect] = useState(false);
  const [showBoolean, setShowBoolean] = useState(false);
  const [showNumericMax, setShowNumericMax] = useState(false);
  const [showSelectNew, setSelectNew] = useState(false);
  const [showNumericPlain, setShowNumericPlain] = useState(false);
  const [showEditAddOn, setShowEditAddOn] = useState(false);
  const [showAddAddOn, setShowAddAddOn] = useState(false);
  const [editModeAccount, setEditModeAccount] = useState(false);
  const [newAccountAdd, setNewAccountAdd] = useState(false);
  const [transferring, setTrransferring] = useState(false);
  const [showBuilding, setShowBuilding] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [error, setError] = useState(false);
  const [allowRoleSelect, setAllowRoleSelect] = useState(false);
  const [showBuildingModal, setshowBuildingModal] = useState(false);
  const [newAccountSelectDisabled, setNewAccountSelectDisabled] =
    useState(false);

  // Logs pagination state
  const accommodationLogsLoadingStatus = useAppSelector(
    selectAccommodationLogsLoadingStatus,
  );
  const initialLogsFilter: AccommodationFilteredHistoryLogsRequest = {
    AggregateId: params.accommodationId,
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: 20,
  };
  const [accommodationLogsFilter, setAccommodationLogsFilter] =
    useState(initialLogsFilter);
  const logsPagination = accommodationLogs
    ? PopulatePagination(accommodationLogs)
    : InitialPaginationResponse;
  const handleLogsPageClick = (Page: number) => {
    const next = { ...accommodationLogsFilter, PageNumber: Page };
    setAccommodationLogsFilter(next);
    dispatch(getAccommodationLogsAsync(next));
  };

  const [accommodationAccountList, setAccommodationAccountList] = useState<
    AccountListItem[]
  >(originalAccommodationAccountList);

  const [editedNewBuilding, setEditedNewBuilding] = useState<AddBuilding>({
    buildingId: '',
    accommodationId: '',
  });
  const travelAgencyDomains: TravelAgencyDomain[] = [
    {
      id: '01089d85-d32f-4136-9d20-1a030a37990e',
      partnerName: 'Saariselkä',
    },
  ];
  const allAccountList = useAppSelector(selectOwnerMaintainerAccounts);
  const accountsLoading = useAppSelector(
    selectOwnerMaintainerAccountsLoadingStatus,
  );
  const [allAccountsForModal, setAllAccountsForModal] =
    useState(allAccountList);
  const [newAttributeDataId, setnewAttributeDataId] = useState('');
  const [editedAttributeRoomId, setEditedAttributeRoomId] = useState('');
  const [editedAttributeRoomAreaId, setEditedAttributeRoomAreaId] =
    useState('');
  const [editedAttributeBuildingId, setEditedAttributeBuildingId] =
    useState('');
  const [editedAttributeItemType, seteditedAttributeItemType] =
    useState<AccommodationAttributeListItem>({
      id: '',
      shortName: '',
      name: '',
      groupName: '',
      index: 0,
      attributeType: {
        id: '',
        typeName: '',
        nameFi: '',
        nameEn: '',
        nameRu: '',
      },
      optionItems: [],
    });

  const [editedBuilding, setEditedBuilding] = useState<BuildingData>({
    id: '',
    rooms: [],
    roomAreas: [],
    translations: [],
    availabilityEndDate: '',
    availabilityStartDate: '',
  });

  const [accommodationNotesState, setAccommodationNotesState] =
    useState<AccommodationNotes>({
      id: '',
      internalNotes: [],
      publicNotes: [],
    });

  const [accommodationAttributesState, setAccommodationAttributesState] =
    useState<AccommodationAttributes>({
      id: '',
      accommodationAttributes: [],
      attributeResponse: '',
      cottageResponse: '',
      otherResponse: '',
      distanceResponse: '',
    });

  const [accommodationAddOnsState, setAccommodationAddOnsState] =
    useState<AccommodationAddOns>({
      id: '',
      addOns: [],
    });

  const Tabs: string[] = [
    'Mökin perustiedot',
    'Omistajat ja huoltajat',
    'Tiedotteet ja viestintä',
    'Rakennukset, Huoneet ja tilat',
    'Kuvaukset',
    'Ominaisuudet / Varustelut',
    'Lisäpalvelut',
    'Varaustilanne',
    'Kuvat',
    'Matkanjärjestäjä',
    'iCal',
    'Muutoslogi',
  ];
  const [activeTab, setActiveInitTab] = useState<string>(
    query.get('tab') || Tabs[0],
  );
  const removeUrlParameters = () => {
    navigate(location.pathname, { replace: true });
  };
  function setActiveTab(tab) {
    setActiveInitTab(tab);
  }

  const [filteringRoomsAttr, setFilteringRoomsAttr] =
    useState<BuildingRoomsQueryFilters>({
      id: '',
      pageSize: 500,
      pageNumber: 1,
    });
  const [filteringAttr, setFilteringAttr] =
    useState<AccommodationAttributeQueryFilters>({
      pageSize: 500,
      pageNumber: 1,
    });
  const [roomsFiltering, setRoomsFiltering] = useState<RoomsAndAreasFilter>({
    pageSize: 300,
    pageNumber: 1,
  });
  const [addOnsFiltering, setAddOnsFiltering] = useState<AddOnsQueryFilters>({
    culture: 'fi_FI',
    PageSize: 500,
    PageNumber: 1,
  });
  const [accommodationBuildingsTemp, setAccommodationBuildingsTemp] = useState<
    BuildingData[]
  >([]);

  function intiateTabAndfech(tab) {
    removeUrlParameters();
    setActiveTab(tab);
    if (tab === 'Omistajat ja huoltajat') {
      dispatch(getAccommodationAccountsAsync(accommodationBasicTemp.id));
      dispatch(
        getAllRolesAsync({
          pageNumber: 1,
          pageSize: 10,
          culture: 'fi_FI',
          searchString: '',
        }),
      );
    }
    if (tab === 'Tiedotteet ja viestintä') {
      dispatch(getAccommodationNotesAsync(accommodationBasicTemp.id));
    }
    if (tab === 'Rakennukset, Huoneet ja tilat') {
      setAccommodationBuildingsTemp([]);
      dispatch(getAllBedsAsync(roomsFiltering));
      dispatch(getAllFloorsAsync(roomsFiltering));
      dispatch(getAllBuildingsAsync(roomsFiltering));
      dispatch(getAllRoomsAsync(roomsFiltering));
      dispatch(getAllRoomAreasAsync(roomsFiltering));
      dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
      dispatch(getAllBuildingRoomsAsync(filteringRoomsAttr));
      dispatch(getAllBuildingRoomAreasAsync(filteringRoomsAttr));
    }
    if (tab === 'Ominaisuudet / Varustelut') {
      const initial = {
        id: '',
        accommodationAttributes: [],
      } as AccommodationAttributes;
      setAccommodationAttributesState(initial);
      dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
      dispatch(getAllAccommodationAttributesAsync(filteringAttr));
      dispatch(getAccommodationAttributesAsync(accommodationBasicTemp.id));
    }
    if (tab === 'Lisäpalvelut') {
      dispatch(getAllAddOnsAsync(addOnsFiltering));
      dispatch(getAccommodationAddOnsAsync(accommodationBasicTemp.id));
      dispatch(getAccommodationAddOnsAccountsAsync(accommodationBasicTemp.id));
      dispatch(
        getAllRolesAsync({
          pageNumber: 1,
          pageSize: 10,
          culture: 'fi_FI',
          searchString: '',
        }),
      );
    }
    if (tab === 'Kuvat') {
      dispatch(getAccommodationImagesAsync(params.accommodationId));
    }
    if (tab === 'Matkanjärjestäjä') {
      const modifiedSearchFilter = {
        PageNumber: InitialPaginationParameters.PageNumber,
        PageSize: 1000,
        searchString: '',
        culture: LOCALES.Fi,
        roleIds: ['61BE6E51-2241-4674-BF7E-ABBAC8BBA433'],
        isActive: true,
      };
      dispatch(getAllAccountsFromDbAsync(modifiedSearchFilter));
    }
    if (tab === 'iCal') {
      // iCal component handles its own data fetching
    }
    if (tab === 'Muutoslogi') {
      const filter: AccommodationFilteredHistoryLogsRequest = {
        AggregateId: params.accommodationId,
        PageNumber: accommodationLogsFilter.PageNumber,
        PageSize: accommodationLogsFilter.PageSize,
      };
      setAccommodationLogsFilter(filter);
      dispatch(getAccommodationLogsAsync(filter));
    }
    setActiveTab(tab);
  }

  const saveBuilding = editedBuildingData => {
    const len =
      accommodationBuildingsTemp != null
        ? accommodationBuildingsTemp.length
        : 0;
    if (len > 0) {
      const updateData = {
        buildingId: editedBuildingData.id,
        availabilityEndDate: editedBuildingData.availabilityEndDate ?? '',
        availabilityStartDate: editedBuildingData.availabilityStartDate ?? '',
        translations: editedBuildingData.translations,
        accommodationId: accommodationBasicTemp.id,
        rooms: editedBuildingData.rooms,
        roomAreas: editedBuildingData.roomAreas,
      } as BuildingUpdateData;
      dispatch(updateBuildingAsync(updateData)).then(res => {
        const newBuildingId = res.payload.toString().toUpperCase();
        filteringRoomsAttr.id = params.accommodationId;
        dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr)).then(
          res2 => {
            const retbuildings = res2.payload;
            setAccommodationBuildingsTemp(retbuildings);
            const activeBuilding = retbuildings.find(
              b => b.id === newBuildingId,
            );
            setEditedBuilding(activeBuilding);
            setshowBuildingModal(true);
          },
        );
      });
    }
  };
  const saveNewBuildingLocal = data => {
    saveBuilding(editedBuilding);
  };
  function transferImagesGrediAzureCdn(accommodationId: string) {
    setTrransferring(true);
    setError(false);
    setErrorText('');
    dispatch(tranferImagesAccommodationAsync(accommodationId)).then(result => {
      setTrransferring(false);
      if (!result.payload.isSuccess) {
        setError(true);
        setErrorText(result.payload.message);
      }
      dispatch(getAccommodationImagesAsync(params.accommodationId)).then(
        result2 => {
          dispatch(getAccommodationDetailsBasicAsync(params.accommodationId));
        },
      );
    });
  }

  function fixImageIndexes() {
    setTrransferring(true);
    setError(false);
    setErrorText('');
    dispatch(fixAccommodationImageIndexesAsync(params.accommodationId)).then(
      result => {
        setTrransferring(false);
        if (!result.payload.isSuccess) {
          setError(true);
          setErrorText(result.payload.message);
        }
        dispatch(getAccommodationImagesAsync(params.accommodationId));
      },
    );
  }

  const [attributeRoomsTemp, setAttributeRoomsTemp] = useState<
    AttributeRoomData[]
  >([]);

  const onDropHandler = (acceptedFiles, accommodationId) => {
    const command = {
      imagesWithFullPath: acceptedFiles,
      accommodationId,
    } as AccommodationUploadImages;
    dispatch(uploadFilesToAzureAsync(command)).then(res => {
      dispatch(getAccommodationImagesAsync(params.accommodationId));
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: acceptedFiles => {
      onDropHandler(acceptedFiles, accommodationDetailsBasic.id);
    },
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
    multiple: true, // Allow multiple files to be selected
  });

  const [attributeRoomareasTemp, setAttributeRoomareasTemp] = useState<
    AttributeRoomAreaData[]
  >([]);
  const [editedAccount, setEditedAccount] = useState<AccommodationAccount>({
    accountId: null,
    roleId: null,
    index: 99,
    id: 'new',
  });

  const initialAccountingFilter: AccountingsQueryFilters = {
    pageNumber: 1,
    pageSize: 100,
    culture: 'fi_FI',
  };

  const [editedAttributeItemData, seteditedAttributeItemData] =
    useState<AccommodationAttributeItem>({
      attributeId: '',
      multiSelectOptions: [],
      optionValue: null,
      floatValue: null,
      intValue: null,
      buildingRoomId: null,
      buildingId: null,
      buildingRoomAreaId: null,
      index: null,
      booleanValue: null,
      textValue: null,
    });

  const editAttributes = (
    accommodationAttributesin: AccommodationAttributeItem[],
    newItem: AccommodationAttributeItem,
  ) => {
    return accommodationAttributesin.map(item => {
      if (item.id === newItem.id) {
        return newItem;
      }
      return item;
    });
  };

  const editedAttributeDataLocal = editedAttributeDataData => {
    setEditedAttributeRoomId(editedAttributeDataData.buildingRoomId ?? '');
    setEditedAttributeBuildingId(editedAttributeDataData.buildingId ?? '');
    setEditedAttributeRoomAreaId(
      editedAttributeDataData.buildingRoomAreaId ?? '',
    );
    seteditedAttributeItemData(editedAttributeDataData);
    const modifiedAttributes = editAttributes(
      accommodationAttributesState.accommodationAttributes,
      editedAttributeDataData,
    );
    const storedAccommodationData = {
      ...accommodationAttributesState,
      accommodationAttributes: modifiedAttributes,
    };
    setAccommodationAttributesState(storedAccommodationData);
  };
  const [editedAddOn, seteditedAddOn] = useState<AccommodationAddonItem>({
    id: null,
    ownerId: null,
    settlementReceiverId: null,
    price: null,
    purchasePrice: null,
    amountSingeCharge: null,
    amountPerQuantity: null,
    amountPerGuest: null,
    durationPerNight: null,
    percentage: null,
    ownerSingleChangePrice: false,
    descriptionTranslations: [],
    isIncludedInPrice: null,
    name: null,
    addOnDefinitionId: null,
    sportumId: '',
    accommodationGroupId: '',
    automatic: false,
    addOnNumber: null,
    translations: [],
  });

  const countryOptions = countries.map(country => {
    return { value: country.id, label: country.name };
  });

  const regionOptions = [
    { value: '', label: 'Ei valintaa' },
    ...regions.map(region => {
      return { value: region.id, label: region.name };
    }),
  ];
  const municipalityOptions = [
    { value: '', label: 'Ei valintaa' },
    ...municipalities.map(municipality => {
      return { value: municipality.id, label: municipality.name };
    }),
  ];
  const skiResortOptions = [
    { value: '', label: 'Ei valintaa' },
    ...skiResorts.map(skiResort => {
      return { value: skiResort.id, label: skiResort.name };
    }),
  ];
  const placeOptions = [
    { value: '', label: 'Ei valintaa' },
    ...places.map(place => {
      return { value: place.id, label: place.name };
    }),
  ];

  function isSelected(itemId) {
    if (addedAccountRoles !== null) {
      return addedAccountRoles.some(s => s.includes(itemId));
    }
    return false;
  }
  const updateBuildingRoomBed = event => {
    dispatch(updateBuildingRoomBedAsync(event)).then(res => {
      dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
    });
  };

  const updateBuildingRoom = event => {
    dispatch(updateBuildingRoomAsync(event)).then(res => {
      dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
    });
  };

  const updateBuildingRoomArea = event => {
    dispatch(updateBuildingRoomAreaAsync(event)).then(res => {
      dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
    });
  };

  function getRoleDescription(roleId) {
    const text = allRoles?.rolesList.find(
      role => role.id === roleId && role.description !== null,
    );
    return text?.description;
  }

  const userRolesOptionsOptions =
    allRoles !== null
      ? allRoles?.rolesList
          .filter(
            role =>
              isSelected(role.id) &&
              role.description !== null &&
              !role.description.includes('Omalii'),
          )
          .map(role => {
            return { value: role.id, label: role.description };
          })
      : [{ value: '', label: '' }];

  const accountOptions = allAccountList.map(account => {
    return {
      value: account.id,
      label: `${account.firstName} ${account.lastName} (${account.roleName})`,
    };
  });
  useEffect(() => {
    if (allAccountList != null && allAccountList.length > 0) {
      setNewAccountSelectDisabled(false);
    }
  }, [allAccountList]);

  useEffect(() => {
    return () => {
      // Clear images from state when changing accommodations
      dispatch({ type: 'accommodations/clearImages' });
    };
  }, [params.id, dispatch]);

  useEffect(() => {}, [editedAddOn]);
  useEffect(() => {
    if (
      originalAccommodationAccountList != null &&
      originalAccommodationAccountList.length > 0
    ) {
      setAccommodationAccountList(originalAccommodationAccountList);
    } else {
      setAccommodationAccountList([]);
    }
  }, [originalAccommodationAccountList]);
  const accommodationAccountOptions = accommodationAccountList.map(account => {
    return {
      value: account.id,
      label: `${account.firstName} ${account.lastName}`,
    };
  });
  const handleCloseSelectNew = () => setSelectNew(false);

  const handleCoordinatesSelect = (Latitude: number, Longitude: number) => {
    setAccommodationLatitude(Latitude);
    setAccommodationLongitude(Longitude);
    const tempData = { ...accommodationBasicTemp };
    const geoTemp: AccommodationGeoCode = {
      latitude: Latitude,
      longitude: Longitude,
    };
    const storedata = { ...tempData, geoCode: geoTemp };
    setAccommodationBasic(storedata);
  };

  const editAddOns = (
    addOnsin: AccommodationAddonItem[],
    newItem: AccommodationAddonItem,
  ) => {
    return addOnsin.map(item => {
      if (item.id === newItem.id) {
        return newItem;
      }
      return item;
    });
  };

  function handleChangeValueFreeText(event) {
    const { target } = event;
    const textValue =
      target.type === 'checkbox' ? target.checked : target.value;
    const tempAttr = {
      ...editedAttributeItemData,
      textValue,
    };
    seteditedAttributeItemData(tempAttr);
    const modifiedAttributes = editAttributes(
      accommodationAttributesState.accommodationAttributes,
      tempAttr,
    );
    const storedAccommodationData = {
      ...accommodationAttributesState,
      accommodationAttributes: modifiedAttributes,
    };
    setAccommodationAttributesState(storedAccommodationData);
  }

  function handleChangeValueAddOn(event) {
    const { target } = event;
    const booleanValue =
      target.type === 'checkbox' ? target.checked : target.value;
    if (target.name === 'singleCharge') {
      const tempAddon = {
        ...editedAddOn,
        amountSingeCharge: booleanValue,
        amountPerQuantity: false,
        amountPerGuest: false,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'perQuantity') {
      const tempAddon = {
        ...editedAddOn,
        amountPerQuantity: booleanValue,
        amountPerGuest: false,
        amountSingeCharge: false,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'automatic') {
      const tempAddon = {
        ...editedAddOn,
        automatic: booleanValue,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'perGuest') {
      const tempAddon = {
        ...editedAddOn,
        amountPerGuest: booleanValue,
        amountPerQuantity: false,
        amountSingeCharge: false,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'perNight') {
      const tempAddon = {
        ...editedAddOn,
        durationPerNight: booleanValue,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'isIncludedInPrice') {
      const tempAddon = {
        ...editedAddOn,
        isIncludedInPrice: booleanValue,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'ownerSingleChange') {
      const tempAddon = {
        ...editedAddOn,
        ownerSingleChangePrice: booleanValue,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'price') {
      const tempAddon = {
        ...editedAddOn,
        price: target.value,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'purchasePrice') {
      const tempAddon = {
        ...editedAddOn,
        purchasePrice: target.value,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    } else if (target.name === 'sportumId') {
      const tempAddon = {
        ...editedAddOn,
        sportumId: target.value,
      };
      seteditedAddOn(tempAddon);
      const modifiedAddOns = editAddOns(
        accommodationAddOnsState.addOns,
        tempAddon,
      );
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: modifiedAddOns,
      };
      setAccommodationAddOnsState(storedAccommodationData);
    }
  }

  function handleChangeValueBoolean(event) {
    const { target } = event;
    const booleanValue =
      target.type === 'checkbox' ? target.checked : target.value;
    const tempAttr = {
      ...editedAttributeItemData,
      booleanValue,
    };
    seteditedAttributeItemData(tempAttr);
    const modifiedAttributes = editAttributes(
      accommodationAttributesState.accommodationAttributes,
      tempAttr,
    );
    const storedAccommodationData = {
      ...accommodationAttributesState,
      accommodationAttributes: modifiedAttributes,
    };
    setAccommodationAttributesState(storedAccommodationData);
  }
  const handleChangeValueMultiSelect = event => {
    const { target } = event;
    // const options = { ...target.options };
    const value = [];
    for (let i = 0, l = event.target.options.length; i < l; i += 1) {
      if (event.target.options[i].selected) {
        const item = {
          value: event.target.options[i].value,
        } as AccommodationOptionValueItem;
        value.push(item);
      }
    }
    const optionValue = target.type === 'checkbox' ? target.checked : value;
    const tempAttr = {
      ...editedAttributeItemData,
      optionValue,
    };
    seteditedAttributeItemData(tempAttr);
    const modifiedAttributes = editAttributes(
      accommodationAttributesState.accommodationAttributes,
      tempAttr,
    );
    const storedAccommodationattributesData = {
      ...accommodationAttributesState,
      accommodationAttributes: modifiedAttributes,
    };
    setAccommodationAttributesState(storedAccommodationattributesData);
  };

  function handleChangeValueInt(event) {
    const { target } = event;
    let intValue = target.type === 'checkbox' ? target.checked : target.value;
    if (intValue <= 0) {
      intValue = 0;
    }
    const tempAttr = {
      ...editedAttributeItemData,
      intValue,
    };
    seteditedAttributeItemData(tempAttr);
    const modifiedAttributes = editAttributes(
      accommodationAttributesState.accommodationAttributes,
      tempAttr,
    );
    const storedAccommodationData = {
      ...accommodationAttributesState,
      accommodationAttributes: modifiedAttributes,
    };
    setAccommodationAttributesState(storedAccommodationData);
  }

  function handleChangeValueFloat(event) {
    const { target } = event;
    let floatValue = target.type === 'checkbox' ? target.checked : target.value;
    if (floatValue < 0) {
      floatValue = 0;
    }
    const tempAttr = {
      ...editedAttributeItemData,
      floatValue,
    };
    seteditedAttributeItemData(tempAttr);
    const modifiedAttributes = editAttributes(
      accommodationAttributesState.accommodationAttributes,
      tempAttr,
    );
    const storedAccommodationData = {
      ...accommodationAttributesState,
      accommodationAttributes: modifiedAttributes,
    };
    setAccommodationAttributesState(storedAccommodationData);
  }

  const handleShowSelectNew = () => {
    setShowBuilding(true);
  };

  const handleShowSelectAttribute = () => {
    setshowAttributesModal(true);
  };

  const handleCloseSelectAttribute = attributeState => {
    setshowAttributesModal(false);
    setAccommodationAttributesState(attributeState);
    const updateData = {
      id: accommodationBasicTemp.id,
      attributes: attributeState.accommodationAttributes,
    } as AccommodationAttributesUpdate;
    dispatch(updateAccommodationsAttributesAsync(updateData)).then(res => {
      dispatch(getAccommodationAttributesAsync(accommodationDetailsBasic.id));
    });
  };

  const handleShowSelectNewAddon = () => {
    const newaddon = {
      id: null,
      ownerId: '',
      name: '',
      addOnDefinitionId: null,
      settlementReceiverId: '',
      percentage: false,
      amountSingeCharge: false,
      amountPerQuantity: false,
      amountPerGuest: false,
      durationPerNight: false,
      price: null,
      ownerSingleChangePrice: false,
      purchasePrice: null,
      isIncludedInPrice: false,
      descriptionTranslations: [],
      translations: [],
      sportumId: '',
      accommodationGroupId: '',
      automatic: false,
      addOnNumber: null,
    } as AccommodationAddonItem;
    seteditedAddOn(newaddon);
    setShowAddAddOn(true);
  };

  const refreshBuildingData = event => {
    filteringRoomsAttr.id = params.accommodationId;
    setFilteringRoomsAttr(filteringRoomsAttr);
    dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
  };

  function getUpdateData(tempData: AccommodationDetailsBasic) {
    const defaultGeoCode = {
      latitude: null,
      longitude: null,
    } as AccommodationGeoCode;
    if (tempData.geoCode == null) {
      tempData = { ...tempData, geoCode: defaultGeoCode };
    }
    const updateAccommodationData: AccommodationDetailsUpdateBasic = {
      id: tempData.id,
      accommodationNumber: tempData.accommodationNumber,
      beds: tempData.beds,
      extraBeds: tempData.extraBeds,
      novelty: tempData.novelty,
      translations: tempData.translations,
      internalQualityRating: tempData.internalQualityRating,
      onSale: tempData.onSale,
      showInOwnerPage: tempData.showInOwnerPage,
      qualityRating: tempData.qualityRating,
      notOnSaleDays: tempData.notOnSaleDays,
      agentNumber: tempData.agentNumber,
      visibleOnFi: tempData.visibleOnFi,
      visibleOnEn: tempData.visibleOnEn,
      visibleOnRu: tempData.visibleOnRu,
      keyBox: tempData.keyBox,
      accommodationGroupDataId: tempData.accommodationGroupData?.id,
      countryId: tempData.countryId,
      regionId: tempData.regionId,
      municipalityId: tempData.municipalityId,
      skiResortId: tempData.skiResortId,
      placeId: tempData.placeId,
      tagId: tempData.tagId,
      fireSafety: tempData.fireSafety,
      geoCode: {
        latitude:
          tempData.geoCode !== null ? (tempData.geoCode.latitude ?? 0) : 0,
        longitude:
          tempData.geoCode !== null ? (tempData.geoCode.longitude ?? 0) : 0,
      },
      optiWatti: tempData.optiWatti,
      leviItem: tempData.leviItem,
      interhomeService: tempData.interhomeService,
      lastMinuteOffer: tempData.lastMinuteOffer,
      sportumId: tempData.sportumId,
      isCapacityProduct: tempData.isCapacityProduct,
      bedRooms: tempData.bedRooms,
      area: tempData.area,
      SalesRewardPerCent: tempData.salesRewardP,
      PriceCommissionPerCent: tempData.priceCommissionP,
      BalanceCommissionPerCent: tempData.balanceCommissionP,
      travelAgencyDomain: tempData.travelAgencyDomain,
      travelAgenciesWithDiscounts: tempData.travelAgenciesWithDiscounts,
      acceptedBySellerSite: tempData.acceptedBySellerSite,
      offeredToSellerSite: tempData.offeredToSellerSite,
      sellerSiteComments: tempData.sellerSiteComments,
    };
    if (
      (tempData.addressId == null || tempData.addressId === '') &&
      addressDetailsTemp !== null &&
      addressDetailsTemp.postStreetAddress.length >= 0
    ) {
      const newitem: UpdateAddressData = {
        accommodationId: tempData.id,
        additionalAddressLine: addressDetailsTemp.additionalAddressLine,
        countryId:
          addressDetailsTemp.countryId === undefined ||
          addressDetailsTemp.countryId === null ||
          addressDetailsTemp.countryId === ''
            ? countryOptions.find(option => option.label === 'SUOMI').value
            : addressDetailsTemp.countryId,
        postCity: addressDetailsTemp.postCity,
        postCode: addressDetailsTemp.postCode,
        description: addressDetailsTemp.description,
        postStreetAddress: addressDetailsTemp.postStreetAddress,
        id: null,
        isPrimary: true,
      };
      const retval = dispatch(addAddressDataAsync(newitem)).then(result => {
        if (result.payload.isSuccess) {
          setAddress(result.payload);
          tempData.addressId = result.payload.addressId;
          setAccommodationBasic(tempData);
          // dispatch(getAccommodationDetailsBasicAsync(params.accommodationId));
        }
      });
    } else if (addressDetailsTemp !== null) {
      const updateItem: UpdateAddressData = {
        accommodationId: tempData.id,
        additionalAddressLine: addressDetailsTemp.additionalAddressLine ?? '',
        countryId:
          addressDetailsTemp.countryId === undefined ||
          addressDetailsTemp.countryId === null ||
          addressDetailsTemp.countryId === ''
            ? countryOptions.find(option => option.label === 'SUOMI').value
            : addressDetailsTemp.countryId,
        postCity: addressDetailsTemp.postCity ?? '',
        postCode: addressDetailsTemp.postCode ?? '',
        description: addressDetailsTemp.description ?? '',
        postStreetAddress: addressDetailsTemp.postStreetAddress ?? '',
        id: addressDetailsTemp.id ?? '',
        isPrimary: true,
      };
      const retval = dispatch(updateAddressDataAsync(updateItem));
    } else {
      return updateAccommodationData;
    }
    return updateAccommodationData;
  }

  function getUpdateDataCross(tempData: AccommodationDetailsBasic) {
    const defaultGeoCode = {
      latitude: null,
      longitude: null,
    } as AccommodationGeoCode;
    if (tempData.geoCode == null) {
      tempData = { ...tempData, geoCode: defaultGeoCode };
    }
    const updateAccommodationData: AccommodationDetailsUpdateBasic = {
      id: tempData.id,
      accommodationNumber: tempData.accommodationNumber,
      beds: tempData.beds,
      extraBeds: tempData.extraBeds,
      novelty: tempData.novelty,
      translations: tempData.translations,
      internalQualityRating: tempData.internalQualityRating,
      onSale: tempData.onSale,
      showInOwnerPage: tempData.showInOwnerPage,
      qualityRating: tempData.qualityRating,
      notOnSaleDays: tempData.notOnSaleDays,
      agentNumber: tempData.agentNumber,
      visibleOnFi: tempData.visibleOnFi,
      visibleOnEn: tempData.visibleOnEn,
      visibleOnRu: tempData.visibleOnRu,
      keyBox: tempData.keyBox,
      accommodationGroupDataId: tempData.accommodationGroupData?.id,
      countryId: tempData.countryId,
      regionId: tempData.regionId,
      municipalityId: tempData.municipalityId,
      skiResortId: tempData.skiResortId,
      placeId: tempData.placeId,
      tagId: tempData.tagId,
      fireSafety: tempData.fireSafety,
      geoCode: {
        latitude:
          tempData.geoCode !== null ? (tempData.geoCode.latitude ?? 0) : 0,
        longitude:
          tempData.geoCode !== null ? (tempData.geoCode.longitude ?? 0) : 0,
      },
      optiWatti: tempData.optiWatti,
      leviItem: tempData.leviItem,
      interhomeService: tempData.interhomeService,
      lastMinuteOffer: tempData.lastMinuteOffer,
      sportumId: tempData.sportumId,
      isCapacityProduct: tempData.isCapacityProduct,
      bedRooms: tempData.bedRooms,
      area: tempData.area,
      SalesRewardPerCent: tempData.salesRewardP,
      PriceCommissionPerCent: tempData.priceCommissionP,
      BalanceCommissionPerCent: tempData.balanceCommissionP,
      travelAgencyDomain: tempData.travelAgencyDomain,
      travelAgenciesWithDiscounts: tempData.travelAgenciesWithDiscounts,
      acceptedBySellerSite: tempData.acceptedBySellerSite,
      offeredToSellerSite: tempData.offeredToSellerSite,
      sellerSiteComments: tempData.sellerSiteComments,
    };
    if (
      (tempData.addressId == null || tempData.addressId === '') &&
      addressDetailsTemp !== null &&
      addressDetailsTemp.postStreetAddress.length >= 0
    ) {
      const newitem: UpdateAddressData = {
        accommodationId: tempData.id,
        additionalAddressLine: addressDetailsTemp.additionalAddressLine,
        countryId:
          addressDetailsTemp.countryId === undefined ||
          addressDetailsTemp.countryId === null ||
          addressDetailsTemp.countryId === ''
            ? countryOptions.find(option => option.label === 'SUOMI').value
            : addressDetailsTemp.countryId,
        postCity: addressDetailsTemp.postCity,
        postCode: addressDetailsTemp.postCode,
        description: addressDetailsTemp.description,
        postStreetAddress: addressDetailsTemp.postStreetAddress,
        id: null,
        isPrimary: true,
      };
      const retval = dispatch(addAddressDataAsync(newitem)).then(result => {
        if (result.payload.isSuccess) {
          setAddress(result.payload);
          tempData.addressId = result.payload.addressId;
          setAccommodationBasic(tempData);
          dispatch(getAccommodationDetailsBasicAsync(params.accommodationId));
        }
      });
    } else if (addressDetailsTemp !== null) {
      const updateItem: UpdateAddressData = {
        accommodationId: tempData.id,
        additionalAddressLine: addressDetailsTemp.additionalAddressLine ?? '',
        countryId:
          addressDetailsTemp.countryId === undefined ||
          addressDetailsTemp.countryId === null ||
          addressDetailsTemp.countryId === ''
            ? countryOptions.find(option => option.label === 'SUOMI').value
            : addressDetailsTemp.countryId,
        postCity: addressDetailsTemp.postCity ?? '',
        postCode: addressDetailsTemp.postCode ?? '',
        description: addressDetailsTemp.description ?? '',
        postStreetAddress: addressDetailsTemp.postStreetAddress ?? '',
        id: addressDetailsTemp.id ?? '',
        isPrimary: true,
      };
    } else {
      return updateAccommodationData;
    }
    return updateAccommodationData;
  }

  function getNotesUpdateData(tempData: AccommodationDetailsBasic) {
    const updateAccommodationData: AccommodationNotesUpdate = {
      id: tempData.id,
      translations: tempData.translations,
      internalNotes: accommodationNotesState.internalNotes,
      publicNotes: accommodationNotesState.publicNotes,
    };
    return updateAccommodationData;
  }
  function updateAddons(existingAddons: AccommodationAddonItem[]) {
    const newArray: AccommodationAddonItem[] = [];
    for (let i = 0, l = existingAddons.length; i < l; i += 1) {
      if (existingAddons[i].id === editedAddOn.id) {
        let item = existingAddons[i];
        item = { ...item, ownerId: editedAddOn.ownerId };
        item = { ...item, addOnDefinitionId: editedAddOn.addOnDefinitionId };
        item = { ...item, price: editedAddOn.price };
        item = { ...item, name: editedAddOn.name };
        item = {
          ...item,
          descriptionTranslations: editedAddOn.descriptionTranslations,
        };
        item = {
          ...item,
          ownerSingleChangePrice: editedAddOn.ownerSingleChangePrice,
        };
        item = { ...item, durationPerNight: editedAddOn.durationPerNight };
        item = { ...item, amountPerGuest: editedAddOn.amountPerGuest };
        item = { ...item, amountPerQuantity: editedAddOn.amountPerQuantity };
        item = { ...item, amountSingeCharge: editedAddOn.amountSingeCharge };
        item = { ...item, isIncludedInPrice: editedAddOn.isIncludedInPrice };
        item = { ...item, percentage: editedAddOn.percentage };
        item = { ...item, sportumId: editedAddOn.sportumId };
        item = {
          ...item,
          accommodationGroupId: editedAddOn.accommodationGroupId,
        };
        item = {
          ...item,
          settlementReceiverId: editedAddOn.settlementReceiverId,
        };
        newArray.push(item);
      } else {
        newArray.push(existingAddons[i]);
      }
    }
    return newArray;
  }

  function fillStoredata() {
    const selectedAttributeroom = buildingRooms.filter(
      rooms => rooms.id === editedAttributeRoomId,
    );
    const selectedAttributeroomArea = buildingRoomAreas.filter(
      roomareas => roomareas.id === editedAttributeRoomAreaId,
    );

    const selectedAttributeBuilding = buildings.filter(
      building => building.id === editedAttributeBuildingId,
    );

    if (selectedAttributeroom!.length! > 0) {
      const tempAttr = {
        ...editedAttributeItemData,
        roomId: selectedAttributeroom[0].id,
      };
      seteditedAttributeItemData(tempAttr);
      const modifiedAttributes = editAttributes(
        accommodationAttributesState.accommodationAttributes,
        tempAttr,
      );
      const storedAccommodationData = {
        ...accommodationAttributesState,
        accommodationAttributes: modifiedAttributes,
      };
      setAccommodationAttributesState(storedAccommodationData);
    }
    if (selectedAttributeroomArea!.length! > 0) {
      const tempAttr = {
        ...editedAttributeItemData,
        roomAreaId: selectedAttributeroomArea[0].id,
      };
      seteditedAttributeItemData(tempAttr);
      const modifiedAttributes = editAttributes(
        accommodationAttributesState.accommodationAttributes,
        tempAttr,
      );
      const storedAccommodationData = {
        ...accommodationAttributesState,
        accommodationAttributes: modifiedAttributes,
      };
      setAccommodationAttributesState(storedAccommodationData);
    }
    if (selectedAttributeBuilding!.length! > 0) {
      const tempAttr = {
        ...editedAttributeItemData,
        buildingId: selectedAttributeBuilding[0].id,
      };
      seteditedAttributeItemData(tempAttr);
      const modifiedAttributes = editAttributes(
        accommodationAttributesState.accommodationAttributes,
        tempAttr,
      );
      const storedAccommodationData = {
        ...accommodationAttributesState,
        accommodationAttributes: modifiedAttributes,
      };
      setAccommodationAttributesState(storedAccommodationData);
    }
  }
  function deleteAccommodationAttribute(removedItem) {
    const tempAttributes =
      accommodationAttributesState?.accommodationAttributes.filter(
        item => item.id !== removedItem.id,
      );
    const storeAccommodationAttributes = {
      ...accommodationAttributesState,
      accommodationAttributes: tempAttributes,
    };
    setAccommodationAttributesState(storeAccommodationAttributes);
    const updateData = {
      id: accommodationBasicTemp.id,
      attributes: storeAccommodationAttributes.accommodationAttributes,
    } as AccommodationAttributesUpdate;
    dispatch(updateAccommodationsAttributesAsync(updateData)).then(res => {
      dispatch(getAccommodationAttributesAsync(accommodationDetailsBasic.id));
    });
  }

  function deleteAddOn(removedItem) {
    const tempAddOns = accommodationAddOnList?.addOns?.filter(
      item => item.id !== removedItem.id,
    );
    const storeAccommodatioAddOns = {
      ...accommodationAddOnList,
      addOns: tempAddOns,
    };
    setAccommodationAddOnsState(storeAccommodatioAddOns);
    const updateAccommodationAddOndata = {
      id: accommodationDetailsBasic.id,
      addOns: storeAccommodatioAddOns.addOns,
    } as AccommodationAddOnsUpdate;
    dispatch(
      updateAccommodationsAddOnsAsync(updateAccommodationAddOndata),
    ).then(res => {
      dispatch(getAccommodationAddOnsAsync(accommodationDetailsBasic.id));
    });
  }

  const deleteBuilding = buildingId => {
    const command = { id: buildingId } as RemoveBuilding;
    dispatch(removeBuildingAsync(command)).then(res => {
      dispatch(getAllAccommodationBuildingsAsync(filteringRoomsAttr));
    });
  };

  const editBuilding = item => {
    setEditedBuilding(item);
    setshowBuildingModal(true);
  };
  const removeImage = imageId => {
    const deleteCommand = {
      imageId,
      accommodationId: accommodationDetailsBasic.id,
    } as DeleteAccommodationImage;
    dispatch(deleteAccommodationImageAsync(deleteCommand)).then(res => {
      dispatch(getAccommodationImagesAsync(accommodationDetailsBasic.id));
    });
  };
  function getAttributeItemType(attributeDefinitionId) {
    let retVal = '';
    if (accommodationAttributesLoadingStatus === LOADING_STATUS.IDLE) {
      const attributeDetails = accommodationAttributes.filter(
        item => attributeDefinitionId === item.id,
      );
      retVal =
        attributeDetails != null && attributeDetails.length > 0
          ? attributeDetails[0].attributeType.typeName
          : '';
    }
    return retVal;
  }
  const GetMultiSelectOptions = (
    optionItems: AccommodationAttributeOptionItem[],
  ) =>
    optionItems?.map((item, index) => (
      <option key={item.nameFi} className="position-relative" value={item.id}>
        {item.nameFi}
      </option>
    ));
  const handleSelectAddBuilding = event => {
    const selectedBuilding = allBuildings.filter(
      building => building.id === event.value,
    );
    if (selectedBuilding != null && selectedBuilding.length > 0) {
      const data = {
        id: selectedBuilding[0].id,
        rooms: [],
        roomAreas: [],
        availabilityStartDate: '',
        availabilityEndDate: '',
        translations: selectedBuilding[0].translations,
      } as BuildingData;
      const addData = {
        accommodationId: params.accommodationId,
        buildingId: selectedBuilding[0].id,
      } as AddBuilding;
      dispatch(addBuildingAsync(addData)).then(res => {
        const newBuildings = [...accommodationBuildingsTemp, res.payload];
        setAccommodationBuildingsTemp(newBuildings);
        setEditedBuilding(res.payload);
      });
    }
  };

  const handleSelectNewAttribute = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const type = getAttributeItemType(value);
    if (type === 'NumericPlain') {
      const newdata = {
        attributeId: value,
      } as AccommodationAttributeItem;
      seteditedAttributeItemData(newdata);
      const data = [
        ...accommodationAttributesState.accommodationAttributes,
        newdata,
      ];
      const storeAccommodationAttributes = {
        ...accommodationAttributesState,
        accommodationAttributes: data,
      };
      setAccommodationAttributesState(storeAccommodationAttributes);
      setShowNumericPlain(true);
    } else if (type === 'FreeText') {
      const newdata = {
        attributeId: value,
      } as AccommodationAttributeItem;
      seteditedAttributeItemData(newdata);
      const data = [
        ...accommodationAttributesState.accommodationAttributes,
        newdata,
      ];
      const storeAccommodationAttributes = {
        ...accommodationAttributesState,
        accommodationAttributes: data,
      };
      setAccommodationAttributesState(storeAccommodationAttributes);
      setShowFreeText(true);
    } else if (type === 'NumericMin') {
      const newdata = {
        attributeId: value,
      } as AccommodationAttributeItem;
      seteditedAttributeItemData(newdata);
      const data = [
        ...accommodationAttributesState.accommodationAttributes,
        newdata,
      ];
      const storeAccommodationAttributes = {
        ...accommodationAttributesState,
        accommodationAttributes: data,
      };
      setAccommodationAttributesState(storeAccommodationAttributes);
      setShowNumericMin(true);
    } else if (type === 'MultiSelect') {
      dispatch(getAccommodationAttributeOptionsAsync(value)).then(result => {
        const newdata = {
          attributeId: value,
          multiSelectOptions: result.payload,
        } as AccommodationAttributeItem;
        seteditedAttributeItemData(newdata);
        const data = [
          ...accommodationAttributesState.accommodationAttributes,
          newdata,
        ];
        const storeAccommodationAttributes = {
          ...accommodationAttributesState,
          accommodationAttributes: data,
        };
        setAccommodationAttributesState(storeAccommodationAttributes);
        setShowMultiSelect(true);
      });
    } else if (type === 'Boolean') {
      const newdata = {
        attributeId: value,
      } as AccommodationAttributeItem;
      seteditedAttributeItemData(newdata);
      const data = [
        ...accommodationAttributesState.accommodationAttributes,
        newdata,
      ];
      const storeAccommodationAttributes = {
        ...accommodationAttributesState,
        accommodationAttributes: data,
      };
      setAccommodationAttributesState(storeAccommodationAttributes);
      setShowBoolean(true);
    } else if (type === 'NumericMax') {
      const newdata = {
        attributeId: value,
      } as AccommodationAttributeItem;
      seteditedAttributeItemData(newdata);
      const data = [
        ...accommodationAttributesState.accommodationAttributes,
        newdata,
      ];
      const storeAccommodationAttributes = {
        ...accommodationAttributesState,
        accommodationAttributes: data,
      };
      setAccommodationAttributesState(storeAccommodationAttributes);
      setShowNumericMax(true);
    }
    setSelectNew(false);
    setnewAttributeDataId(value);
  };
  function deleteAccountObject(accountObject) {
    const tempItem = accommodationAccountList.find(
      accAcco => accAcco.id === accountObject.id,
    );
    if (!tempItem) {
      return;
    }
    const ownersTemp = [...accommodationAccountList];
    const itemIndex = ownersTemp.findIndex(o => o.id === accountObject.id);
    if (itemIndex > -1) {
      ownersTemp.splice(itemIndex, 1);
    }
    const updateData = {
      id: accommodationDetailsBasic.id,
      updateAccommodationAccountRoleCollection: ownersTemp.map(
        item =>
          ({
            accountId: item.accountId,
            roleId: item.roleId,
            index: item.index,
            id: item.id,
          }) as AccommodationAccount,
      ),
    } as AccommodationAccountsUpdate;
    dispatch(updateAccommodationAccountsAsync(updateData)).then(res => {
      dispatch(getAccommodationAccountsAsync(accommodationDetailsBasic.id));
    });
  }

  function openAccountEdit(accountObject) {
    const tempItem = accommodationAccountList.find(
      accAcco => accAcco.id === accountObject.id,
    );
    if (!tempItem) {
      return;
    }
    dispatch(getAccountRolesFromDbAsync(tempItem.accountId));
    setEditModeAccount(true);
    setEditedAccount({
      accountId: tempItem.accountId,
      id: tempItem.id,
      roleId: tempItem.roleId,
      index: tempItem.index,
    } as AccommodationAccount);
    setNewAccountSelectDisabled(false);
    setIsAddAccountModalVisible(true);
  }
  const handleNavigation = (accountId, from) => {
    navigate(`/users/details/${accountId}`, { state: { from } });
  };

  const accountsSelectOptions =
    accommodationAccountList !== null ? (
      accommodationAccountList.map(item => (
        <tr key={item.id || Math.random()}>
          <td>{item.accountNumber}</td>
          <td>
            <Button
              className="table-link btn btn-link p-0 btn-sm text-start"
              onClick={() =>
                handleNavigation(item.accountId, location.pathname)
              }
            >
              {item.firstName} {item.lastName}
            </Button>
          </td>
          <td>
            {getRoleDescription(item.roleId)}
            {(item.index === 88 || item.index === 77) && ' (Lisäpalvelulta)'}
          </td>

          <td>
            {item && item.roleId && (
              <span
                className={`bi bi-${
                  item.roleId.toUpperCase() === OWNER_ROLE.Customer &&
                  item.index === 0
                    ? 'check-lg text-success'
                    : 'x-lg text-danger'
                } me-4`}
              >
                <span className="visually-hidden">
                  {item.roleId.toUpperCase() === OWNER_ROLE.Customer &&
                  item.index === 0
                    ? 'Kyllä'
                    : 'Ei'}
                </span>
              </span>
            )}
          </td>
          <td style={{ width: 100 }}>
            <Button
              variant="outline-blue"
              disabled={
                !accommodationBasicTemp.isAllowedToEdit ||
                item.index === 77 ||
                item.index === 88
              }
              size="sm"
              onClick={() => openAccountEdit(item)}
            >
              Muokkaa
            </Button>
          </td>
          <td style={{ width: 48 }}>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={
                !accommodationBasicTemp.isAllowedToEdit ||
                item.index === 77 ||
                item.index === 88
              }
              onClick={() => deleteAccountObject(item)}
            >
              <span className="bi bi-trash-fill">
                <span className="visually-hidden">Poista</span>
              </span>
            </Button>
          </td>
        </tr>
      ))
    ) : (
      <div />
    );

  function handleShowEditAddon(item, id) {
    seteditedAddOn(item);
    setShowEditAddOn(true);
  }

  function handleShowNumericPlain(item, id) {
    const attributeDetails = accommodationAttributes.filter(
      itemv => id === itemv.id,
    );
    seteditedAttributeItemData(item);
    seteditedAttributeItemType(attributeDetails[0]);
    setShowNumericPlain(true);
  }

  function handleShowFreeText(item, id) {
    const attributeDetails = accommodationAttributes.filter(
      itemv => id === itemv.id,
    );
    seteditedAttributeItemData(item);
    seteditedAttributeItemType(attributeDetails[0]);
    setShowFreeText(true);
  }

  function handleShowNumericMin(item, id) {
    const attributeDetails = accommodationAttributes.filter(
      itemv => id === itemv.id,
    );
    seteditedAttributeItemData(item);
    seteditedAttributeItemType(attributeDetails[0]);
    setShowNumericMin(true);
  }

  function handleShowMultiSelect(item, id) {
    const attributeDetails = accommodationAttributes.filter(
      itemv => id === itemv.id,
    );
    seteditedAttributeItemData(item);
    seteditedAttributeItemType(attributeDetails[0]);
    setShowMultiSelect(true);
  }

  function handleShowBoolean(item, id) {
    const attributeDetails = accommodationAttributes.filter(
      itemv => id === itemv.id,
    );
    seteditedAttributeItemData(item);
    seteditedAttributeItemType(attributeDetails[0]);
    setShowBoolean(true);
  }

  function handleShowNumericMax(item, id) {
    const attributeDetails = accommodationAttributes.filter(
      itemv => id === itemv.id,
    );
    seteditedAttributeItemData(item);
    seteditedAttributeItemType(attributeDetails[0]);
    setShowNumericMax(true);
  }
  const [accountsFiltering, setAccountsFiltering] = useState<AccountsFilter>({
    culture: 'fi_FI',
    PageSize: 200,
    PageNumber: 1,
    searchString: '',
  });
  function getIndex() {
    // Return the smallest non-negative index not currently in use
    const usedIndexes = new Set<number>(
      (accommodationAccountList ?? [])
        .map(a => (typeof a.index === 'number' ? a.index : Number(a.index)))
        .filter(n => Number.isFinite(n) && n >= 0) as number[],
    );

    let next = 0;
    while (usedIndexes.has(next)) {
      next += 1;
    }
    return next;
  }
  function openAddModal() {
    setNewAccountSelectDisabled(true);
    setNewAccountAdd(true);
    setAllowRoleSelect(false);
    const filter = {
      ...accountsFiltering,
      searchString: '',
    };
    setAccountsFiltering(filter);
    setEditedAccount({
      accountId: null,
      index: getIndex(),
      roleId: null,
      id: 'new',
    } as AccommodationAccount);
    setIsAddAccountModalVisible(!isAddAccountModalVisible);
  }
  function changeOldOwnerBookingsToNewOwner() {
    if (
      window.confirm(
        'Haluatko varmasti siirtää omistajan omat ja väliaikaiset varaukset omistajalle, jolla on indeksi 0?',
      )
    ) {
      dispatch(moveOwnerBookingsToNewOwnerAsync(params.accommodationId)).then(
        res => {
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(
              addStatusToast({
                Message: `Omistaja muutettu.`,
                Title: 'Ok',
                IsAutohide: false,
              }),
            );
          } else {
            dispatch(
              addStatusToast({
                IsFailed: true,
                Message:
                  res.payload.message ?? 'Virhe muuoksen suorittamisessa',
                Title: 'Ok',
                IsAutohide: false,
              }),
            );
          }
        },
      );
    }
  }
  function changeMaintainerBookingsToNewMaintainer() {
    if (
      window.confirm(
        'Haluatko varmasti siirtää voimassa olevat varaukset uudelle huoltajalle?',
      )
    ) {
      dispatch(
        moveMaintainerBookingsToNewMaintainerAsync(params.accommodationId),
      ).then(res => {
        if (res.meta.requestStatus === 'fulfilled') {
          dispatch(
            addStatusToast({
              Message: `Huoltaja muutettu.`,
              Title: 'Ok',
              IsAutohide: false,
            }),
          );
        } else {
          dispatch(
            addStatusToast({
              IsFailed: true,
              Message: res.payload.message ?? 'Virhe muuoksen suorittamisessa',
              Title: 'Ok',
              IsAutohide: false,
            }),
          );
        }
      });
    }
  }
  const handleInputChangeSelectFilter = event => {
    const filter = {
      ...accountsFiltering,
      searchString: event.target.value,
      roleIds: ['owner', 'maintainer'],
    };
    setAccountsFiltering(filter);
  };
  const accountsSelectSearch = event => {
    setNewAccountSelectDisabled(true);
    dispatch(getOwnerMaintainerAccountsAsync(accountsFiltering));
  };
  const accommodationDetailsLoadingStatus = useAppSelector(
    selectAccommodationDetailsLoadingStatus,
  );
  const [isSyncingBasic, setIsSyncingBasic] = useState(false);
  const accommodationsAttributes = useAppSelector(
    selectAccommodationsAttributes,
  );

  const [filtering, setFiltering] =
    useState<AccommodationFilteredHistoryLogsRequest>({
      AggregateId: null,
      PageSize: 20,
      PageNumber: 1,
    });
  const [filteringGroup] = useState<AccommodationGroupQueryFilters>({
    PageSize: 200,
    PageNumber: 1,
    isDeleted: false,
  });
  function getItem(locale: string, keyId: string, item) {
    if (
      accommodationDetailsLoadingStatus === LOADING_STATUS.IDLE &&
      item !== undefined &&
      item !== null &&
      item.translations !== null &&
      item.translations !== undefined
    ) {
      const found = item.translations.filter(
        a => a.locale === locale && a.keyId === keyId,
      );
      if (found && found[0] && found[0].text) {
        return found[0].text;
      }
    }
    return '';
  }
  const allBuildingOptions =
    allBuildings != null
      ? allBuildings.map(item => {
          return {
            value: item.id,
            label: getItem('fi_FI', 'BuildingName', item),
          };
        })
      : [];

  function getAttributeItem(attributeDefinitionId) {
    let retVal = '';
    const attributeDetails = accommodationAttributes.filter(
      item => attributeDefinitionId === item.id,
    );
    if (attributeDetails !== null && attributeDetails.length > 0) {
      retVal = attributeDetails[0].name;
    }
    return retVal;
  }

  function getAttributeIsVarustelu(attributeDefinitionId) {
    const retVal = '';
    const attributeDetails = accommodationAttributes.filter(
      item =>
        attributeDefinitionId === item.id && item.groupName === 'Varustelu',
    );
    return attributeDetails !== null && attributeDetails.length > 0;
  }

  function getAttributeIsDistances(attributeDefinitionId) {
    const retVal = '';
    const attributeDetails = accommodationAttributes.filter(
      item =>
        attributeDefinitionId === item.id && item.groupName === 'Etäisyydet',
    );
    return attributeDetails !== null && attributeDetails.length > 0;
  }

  function getAttributeIsOther(attributeDefinitionId) {
    const retVal = '';
    const attributeDetails = accommodationAttributes.filter(
      item =>
        attributeDefinitionId === item.id &&
        item.groupName === 'AlasvetoValikkoMuut',
    );
    return attributeDetails !== null && attributeDetails.length > 0;
  }

  function getAttributeIsMokinTiedot(attributeDefinitionId) {
    const retVal = '';
    const attributeDetails = accommodationAttributes.filter(
      item =>
        attributeDefinitionId === item.id && item.groupName === 'Mökin tiedot',
    );
    return attributeDetails !== null && attributeDetails.length > 0;
  }

  function getAttributeItemValue(attributeDefinitionId, itemv) {
    let retVal = '';
    const attributeDetails = accommodationAttributes.filter(
      item => attributeDefinitionId.toUpperCase() === item.id.toUpperCase(),
    );
    if (
      attributeDetails === null ||
      attributeDetails === undefined ||
      attributeDetails.length === 0
    ) {
      return '';
    }
    if (
      attributeDetails !== null &&
      attributeDetails.length > 0 &&
      attributeDetails[0].attributeType.typeName === 'NumericPlain'
    ) {
      retVal =
        itemv.intValue !== null && itemv.intValue !== undefined
          ? itemv.intValue.toString()
          : '';
    } else if (
      attributeDetails !== null &&
      attributeDetails.length > 0 &&
      attributeDetails[0].attributeType.typeName === 'FreeText'
    ) {
      retVal =
        itemv.textValue !== null && itemv.textValue !== undefined
          ? itemv.textValue
          : '';
    } else if (
      attributeDetails !== null &&
      attributeDetails.length > 0 &&
      attributeDetails[0].attributeType.typeName === 'NumericMin'
    ) {
      retVal =
        itemv.floatValue !== null && itemv.floatValue !== undefined
          ? itemv.floatValue.toString()
          : '';
      if (
        itemv.floatValue !== null &&
        itemv.floatValue !== undefined &&
        attributeDetails[0].groupName === 'Etäisyydet'
      ) {
        retVal = `${itemv.floatValue}`;
      }
    } else if (
      attributeDetails !== null &&
      attributeDetails.length > 0 &&
      attributeDetails[0].attributeType.typeName === 'MultiSelect'
    ) {
      if (
        itemv.optionValue !== null &&
        itemv.optionValue !== undefined &&
        typeof itemv.optionValue === 'object'
      ) {
        if (itemv.optionValue.length > 0) {
          for (let i = 0, l = itemv.optionValue.length; i < l; i += 1) {
            const optionId = itemv.optionValue[i].value;
            const val = itemv.multiSelectOptions.find(
              iter => iter.id === optionId,
            );
            if (val !== null && val !== undefined) {
              if (val.nameFi !== null && val.nameFi !== undefined) {
                retVal +=
                  val !== null && val.nameFi !== null ? (val.nameFi ?? '') : '';
                retVal += ', ';
              }
            }
          }
        }
      } else if (
        itemv != null &&
        itemv.multiSelectOptions != null &&
        itemv.optionValue != null
      ) {
        retVal = itemv.multiSelectOptions[itemv.optionValue].nameFi;
      }
    } else if (
      attributeDetails !== null &&
      attributeDetails.length > 0 &&
      attributeDetails[0].attributeType.typeName === 'Boolean'
    ) {
      retVal =
        itemv.booleanValue !== null && itemv.booleanValue !== undefined
          ? itemv.booleanValue.toString()
          : false;
    } else if (
      attributeDetails !== null &&
      attributeDetails.length > 0 &&
      attributeDetails[0].attributeType.typeName === 'NumericMax'
    ) {
      retVal =
        itemv.floatValue !== null && itemv.floatValue !== undefined
          ? itemv.floatValue.toString()
          : '';
      if (
        itemv.floatValue !== null &&
        itemv.floatValue !== undefined &&
        attributeDetails[0].groupName === 'Etäisyydet'
      ) {
        retVal = `${itemv.floatValue}`;
      }
    }
    return retVal;
  }
  const hasOwnerIBAN = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    if (name === 'onSale' && value === true) {
      // Sanity Check
      const foundItem = accommodationAccountList.filter(
        ac =>
          ac.index === 0 &&
          ac.roleId.toUpperCase() === OWNER_ROLE.Customer.toUpperCase(),
      );
      if (
        foundItem !== null &&
        foundItem !== undefined &&
        foundItem.length > 0
      ) {
        if (!foundItem[0].hasBankIbanLength) {
          return false;
        }
        return true;
      }
      return false;
    }
    return true;
  };

  const hasSeasonRates = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    if (name === 'onSale' && value === true) {
      // Sanity Check
      const currentDate = new Date();
      const filteredSeasonRateItems = seasonRatesFetched?.filter(item => {
        const endDate = new Date(item.endDate);
        return endDate >= currentDate;
      });
      if (!filteredSeasonRateItems || filteredSeasonRateItems.length <= 0) {
        return false;
      }
    }
    return true;
  };
  const handleInputChange = event => {
    const { target } = event;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    if (target.type === 'number') {
      if (value < 0) {
        value = 0;
      }
    }
    if (name === 'onSale' && value === true) {
      dispatch(
        getAccommodationOwnerValidationsAsync(params.accommodationId),
      ).then(res => {
        if (res.meta.requestStatus === 'fulfilled') {
          if (res.payload.hasEmail === false) {
            value = false;
            dispatch(
              addStatusToast({
                Message: 'Kohteen omistajalla ei ole sähköpostia!!',
                IsFailed: true,
                Title: 'Virhe',
                IsAutohide: false,
              }),
            );
          }
          if (res.payload.hasPhone === false) {
            value = false;
            dispatch(
              addStatusToast({
                Message: 'Kohteen omistajalla ei ole puhelin numeroa!!',
                IsFailed: true,
                Title: 'Virhe',
                IsAutohide: false,
              }),
            );
          }
          if (res.payload.hasAddress === false) {
            value = false;
            dispatch(
              addStatusToast({
                Message: 'Kohteen omistajalla ei ole Osoitetta!!',
                IsFailed: true,
                Title: 'Virhe',
                IsAutohide: false,
              }),
            );
          }
          if (res.payload.hasBookingNote === false) {
            value = false;
            dispatch(
              addStatusToast({
                Message: 'Kohteen omistajalla ei ole varaustiedote asetuksia!!',
                IsFailed: true,
                Title: 'Virhe',
                IsAutohide: false,
              }),
            );
          }
        }
      });
      if (!hasSeasonRates(event)) {
        value = false;
        dispatch(
          addStatusToast({
            Message: 'Kohteella ei ole voimassa olevia hintakausia!!',
            IsFailed: true,
            Title: 'Virhe',
            IsAutohide: false,
          }),
        );
      }
      if (!hasOwnerIBAN(event)) {
        value = false;
        dispatch(
          addStatusToast({
            Message: 'Omistajalla ei ole pankkitiliä!!',
            IsFailed: true,
            Title: 'Virhe',
            IsAutohide: false,
          }),
        );
      }
      // Sanity Check
      if (
        accommodationBasicTemp.accommodationGroupData === null ||
        accommodationBasicTemp.accommodationGroupData === undefined
      ) {
        value = false;
        dispatch(
          addStatusToast({
            Message: 'Majoitusryhmää ei ole valittu',
            IsFailed: true,
            Title: 'Virhe',
          }),
        );
      }
      if (
        accommodationAccountOptions === null ||
        accommodationAccountOptions === undefined ||
        accommodationAccountOptions.length <= 0
      ) {
        value = false;
        dispatch(
          addStatusToast({
            Message: 'Omistajia ei ole valittu',
            IsFailed: true,
            Title: 'Virhe',
          }),
        );
      }
      if (
        accommodationAccountOptions === null ||
        accommodationAccountOptions === undefined ||
        accommodationAccountOptions.length <= 0
      ) {
        value = false;
        dispatch(
          addStatusToast({
            Message: 'Omistajia ei ole valittu',
            IsFailed: true,
            Title: 'Virhe',
          }),
        );
      }
      if (
        accommodationAccountOptions === null ||
        accommodationAccountOptions === undefined ||
        accommodationAccountOptions.length > 0
      ) {
        const exist = accommodationAccountList.filter(
          aal =>
            aal.roleId.toUpperCase() ===
            '2257DF05-B62A-4DEA-B561-80F77595D188'.toUpperCase(),
        );
        if (!exist || exist.length <= 0) {
          value = false;
          dispatch(
            addStatusToast({
              Message: 'Omistajia ei ole valittu',
              IsFailed: true,
              Title: 'Virhe',
            }),
          );
        }
      }
    }
    const tempdata = { ...accommodationBasicTemp, [name]: value };
    setAccommodationBasic(tempdata);
  };

  const handleInputChangeGeoCode = event => {
    const { target } = event;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const tempdata = { ...accommodationBasicTemp };
    if (value === '') {
      value = 0;
    }
    const { geoCode } = tempdata;
    const geoTemp = { ...geoCode, [name]: value };
    const storedata = { ...tempdata, geoCode: geoTemp };
    if (name === 'latitude') {
      setAccommodationLatitude(value);
    } else if (name === 'longitude') {
      setAccommodationLongitude(value);
    }
    setAccommodationBasic(storedata);
  };

  const handleSelectChangeAddress = (selectedOption, name) => {
    const tempAccommodation = { ...addressDetailsTemp };
    tempAccommodation[name] = selectedOption.value;
    setAddress(tempAccommodation);
  };
  const editAccounts = (
    accountsIn: AccountListItem[],
    valuedata: AccountListItem,
  ) => {
    if (valuedata.id !== 'new') {
      return accountsIn.map(item => {
        if (item.id === valuedata.id) {
          item = valuedata;
        }
        return item;
      });
    }
    accountsIn = [...accountsIn, valuedata];
    return accountsIn;
  };
  const editAccountsExist = (
    accountsIn: AccountListItem[],
    valuedata: AccountListItem,
  ) => {
    return accountsIn.map(item => {
      if (item.id === valuedata.id) {
        item = valuedata;
      }
      return item;
    });
    return accountsIn;
  };
  const editAccountsRole = (
    accountsIn: AccountListItem[],
    valuedata: AccountListItem,
  ) => {
    return accountsIn.map(item => {
      if (item.id === valuedata.id) {
        item = valuedata;
      }
      return item;
    });
    return accountsIn;
  };
  const handleSelectChangeRoleToPickedAccount = (selectedOption, name) => {
    if (selectedOption === null || selectedOption === undefined) {
      return;
    }

    // If selected role is the special role and there is no account with that role at index 0, set index to 0
    const SPECIAL_ROLE_ID = '2257DF05-B62A-4DEA-B561-80F77595D188';
    const selectedRoleIdLower = String(
      selectedOption.value ?? '',
    ).toLowerCase();
    const specialRoleLower = SPECIAL_ROLE_ID.toLowerCase();

    let nextIndex = editedAccount?.index;
    if (selectedRoleIdLower === specialRoleLower) {
      const hasIndexZeroForRole = (accommodationAccountList ?? []).some(
        acc =>
          String(acc?.roleId ?? '').toLowerCase() === specialRoleLower &&
          Number(acc?.index) === 0,
      );
      if (!hasIndexZeroForRole) {
        nextIndex = 0;
      }
    }
    const storeAccount = {
      ...editedAccount,
      roleId: selectedOption.value,
      index: nextIndex,
    };
    setEditedAccount(storeAccount);
    const ownersTemp = accommodationAccountList;
    if (ownersTemp.filter(acc => acc.id === editedAccount.id).length > 0) {
      const editedAccountsFromAccountList = ownersTemp.filter(
        acc => acc.id === editedAccount.id,
      );
      if (editedAccountsFromAccountList.length > 0) {
        let editedListAccount = editedAccountsFromAccountList[0];
        editedListAccount = {
          ...editedListAccount,
          id: storeAccount.id,
          accountId: storeAccount.accountId,
          roleId: storeAccount.roleId,
          index: storeAccount.index,
        };
        const storeaccountList = editAccountsRole(
          ownersTemp,
          editedListAccount,
        );
        dispatch(getAccountRolesFromDbAsync(storeAccount.accountId));
        setAccommodationAccountList(storeaccountList);
        setNewAccountSelectDisabled(false);
      }
    }
  };
  const handleSelectChangeOwnerPick = (selectedOption, name) => {
    const ownersTemp = accommodationAccountList;
    if (selectedOption === null || selectedOption === undefined) {
      return;
    }
    if (
      ownersTemp.filter(acc => acc.id === editedAccount.id || acc.id === 'new')
        .length > 0
    ) {
      const editedAccountsFromAccountList = ownersTemp.filter(
        acc => acc.id === editedAccount.id,
      );
      if (editedAccountsFromAccountList.length > 0) {
        const editedAccountTemp = {
          accountId: selectedOption.value,
          roleId: '00000000-0000-0000-0000-000000000000',
          index: editedAccountsFromAccountList[0].index,
          id: editedAccountsFromAccountList[0].id,
        } as AccommodationAccount;
        let editedListAccount = editedAccountsFromAccountList[0];
        editedListAccount = {
          ...editedListAccount,
          id: editedAccountTemp.id,
          accountId: selectedOption.value,
          lastName: selectedOption.label,
          firstName: '',
          userName: '',
          roleId: '',
          index: 99,
        };
        const storeaccountList = editAccountsExist(
          ownersTemp,
          editedListAccount,
        );
        dispatch(getAccountRolesFromDbAsync(selectedOption.value));
        console.log('store2:', storeaccountList);
        setAccommodationAccountList(storeaccountList);
        setEditedAccount(editedAccountTemp);
        setNewAccountSelectDisabled(true);
      }
    } else {
      const newAccountTemp = {
        accountId: selectedOption.value,
        roleId: '00000000-0000-0000-0000-000000000000',
        index: editedAccount.index,
        id: 'new',
      } as AccommodationAccount;
      const neweditedListAccount = {
        id: newAccountTemp.id,
        accountId: selectedOption.value,
        lastName: selectedOption.label,
        firstName: '',
        userName: '',
        roleId: '',
        index: 99,
      } as AccountListItem;
      const storeaccountList = editAccounts(ownersTemp, neweditedListAccount);
      dispatch(getAccountRolesFromDbAsync(selectedOption.value));
      setAccommodationAccountList(storeaccountList);
      setEditedAccount(newAccountTemp);
      setNewAccountSelectDisabled(true);
    }
  };
  const handleSelectChangeAccommodationLocation = (selectedOption, name) => {
    const accommodationTempData = { ...accommodationBasicTemp };
    accommodationTempData[name] = selectedOption.value;
    setAccommodationBasic(accommodationTempData);
  };
  const handleSelectChangeGroup = (selectedOption, name) => {
    let groupTemp = accommodationBasicTemp.accommodationGroupData;
    const selectedValue =
      selectedOption && typeof selectedOption.value === 'string'
        ? selectedOption.value.toUpperCase()
        : selectedOption?.value;
    groupTemp = { ...groupTemp, [name]: selectedValue };
    const storeAccommodation = { ...accommodationBasicTemp };
    storeAccommodation.accommodationGroupData = groupTemp;
    setAccommodationBasic(storeAccommodation);
  };

  const handleTravelAgencyDomainSelectChange = selectedOption => {
    const storeAccommodation = { ...accommodationBasicTemp };
    if (selectedOption == null) {
      storeAccommodation.travelAgencyDomain = null;
    } else {
      storeAccommodation.travelAgencyDomain = selectedOption.value;
    }
    setAccommodationBasic(storeAccommodation);
  };
  const handleTravelAgencyDiscountSelectChange = selectedOptions => {
    const storeAccommodation = { ...accommodationBasicTemp };
    if (selectedOptions == null || selectedOptions.length === 0) {
      storeAccommodation.travelAgenciesWithDiscounts = null;
    } else {
      storeAccommodation.travelAgenciesWithDiscounts = selectedOptions.map(
        option => option.value,
      );
    }
    setAccommodationBasic(storeAccommodation);
  };

  /*
  const handleCommissionGroupSelect = (commissionGroupId: string) => {
    const updatedBasicTempData = { ...accommodationBasicTemp };
    updatedBasicTempData.commissionGroupId = commissionGroupId;
    setAccommodationBasic(updatedBasicTempData);
  };
  */

  const handleTagSelect = (tagId: string) => {
    const updatedBasicTempData = { ...accommodationBasicTemp };
    updatedBasicTempData.tagId = tagId;
    setAccommodationBasic(updatedBasicTempData);
  };

  const handleSelectChangeGroupAddOn = (selectedOption, name) => {
    const groupTemp = {
      ...editedAddOn,
      accommodationGroupId: selectedOption ? selectedOption.value : null, // Set to null when cleared
    };
    seteditedAddOn(groupTemp);
  };

  function addInternalNote() {
    let notesTemp = accommodationNotes.internalNotes;
    const newItem: AccommodationInternalNote = {
      id: null,
      isDeleted: false,
      noteKey: 'AccommodationInternalInformation',
      noteText: '',
    };
    notesTemp = [...notesTemp, newItem];
    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.internalNotes = notesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  }

  const getAccommodationPropertiesText = async (): Promise<string> => {
    let propertiesText = '';

    // Attributes from local state
    if (accommodationAttributesState) {
      const propertyInfo = `${accommodationAttributesState.cottageResponse ?? ''},${accommodationAttributesState.attributeResponse ?? ''},${accommodationAttributesState.otherResponse ?? ''}`;
      let watersText = 'Vesistön tyyppi: ';
      propertyInfo.split(',').forEach(prop => {
        if (
          prop &&
          !prop.toLowerCase().includes('tupakointi') &&
          !propertiesText.includes(prop) &&
          !propertiesText.includes('kielletty')
        ) {
          if (
            prop.toLocaleLowerCase().includes('järvi kuistilta') &&
            !watersText.includes('järvi')
          ) {
            watersText += 'järvi';
          }
          if (
            prop.toLocaleLowerCase().includes('meren') &&
            !watersText.includes('meren')
          ) {
            watersText += 'meri';
          }
          propertiesText += `\n${prop}`;
        }
      });
      propertiesText += `\n${watersText}`;
      if (accommodationAttributesState.distanceResponse) {
        propertiesText += `\n\nEtäisyydet:\n${accommodationAttributesState.distanceResponse}`;
      }
    }

    // Basic details from Redux state
    if (accommodationDetailsBasic) {
      const { beds, extraBeds } = accommodationDetailsBasic;
      if (beds) propertiesText += `\n\nVuoteita yhteensä: ${beds}`;
      if (extraBeds)
        propertiesText += `\n\nLisävuoteita yhteensä: ${extraBeds}`;

      const municipalityName = municipalities.find(
        m => m.id === accommodationDetailsBasic.municipalityId,
      )?.name;
      const regionName = regions.find(
        r => r.id === accommodationDetailsBasic.regionId,
      )?.name;
      const skiResortName = accommodationDetailsBasic.skiResortId
        ? skiResorts.find(s => s.id === accommodationDetailsBasic.skiResortId)
            ?.name
        : undefined;

      if (municipalityName) propertiesText += `\n\nKunta: ${municipalityName}`;
      if (regionName) propertiesText += `\n\nAlue: ${regionName}`;
      if (skiResortName) propertiesText += `\n\nHiihtokeskus: ${skiResortName}`;

      // Fetch customer feedback and calculate average rating
      try {
        const feedbackResponse = await fetchFeedbacks({
          accommodationId: accommodationDetailsBasic.id,
          ownerId: '',
          orderBy: 0,
          OrderDirection: 0,
          FeedbackFetchAnswerStartDate: new Date(2000, 0, 1),
          FeedbackFetchAnswerEndDate: new Date(),
          handlerName: '',
          feedbackState: 'Approved',
          PageNumber: 1,
          PageSize: 400,
        });
        const feedbacks = feedbackResponse?.data;
        if (feedbacks && feedbacks.length > 0) {
          const ratingsWithValue = feedbacks.filter(f => f.rating > 0);
          if (ratingsWithValue.length > 0) {
            const avgRating =
              ratingsWithValue.reduce((sum, f) => sum + f.rating / 2, 0) /
              ratingsWithValue.length;
            propertiesText += `\n\nAsiakasarvostelujen keskiarvo: ${avgRating.toFixed(1)}`;
          }
        }
      } catch {
        // Feedback fetch failed — continue without review data
      }
    }

    return propertiesText.trim();
  };

  const rewriteAccommodationDescription = async (
    description: string,
    type: 'properties' | 'description' | 'both',
  ): Promise<N8Nresponse> => {
    let text = '';
    switch (type) {
      case 'properties':
        text = await getAccommodationPropertiesText();
        break;

      case 'description':
        text = description;
        break;

      case 'both': {
        const propertiesText = await getAccommodationPropertiesText();
        text = `Mökin kuvaus:\n${description}\n\nMökin tiedot:\n${propertiesText}`;
        break;
      }

      default:
        text = description;
        break;
    }
    const command: RewriteDescriptionCommand = {
      description: text,
    };
    const rewriteResponse = await dispatch(rewriteDescriptionAsync(command));

    if (!rewriteResponse.payload) {
      return {
        message: 'Kuvauksen parantaminen epäonnistui',
        isSuccess: false,
      } as N8Nresponse;
    }

    return {
      message: rewriteResponse.payload.message,
      isSuccess: rewriteResponse.payload.isSuccess,
    } as N8Nresponse;
  };

  const translateAccommodationDescription = async (
    description: string,
    targetLanguage: string,
  ): Promise<N8Nresponse> => {
    const command: TranslatePropertiesCommand = {
      text: description,
      targetLanguage,
    };
    const translationResponse = await dispatch(
      translateDescriptionAsync(command),
    );
    return {
      message: translationResponse.payload.message,
      isSuccess: translationResponse.payload.isSuccess,
    } as N8Nresponse;
  };

  function addPublicNote() {
    let notesTemp = accommodationNotesState.publicNotes;
    const newItem: AccommodationDetailsPublicNotes = {
      id: null,
      noteText: '',
      visibilityStartsAt: null,
      visibilityEndsAt: null,
    };
    notesTemp = [...notesTemp, newItem];
    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.publicNotes = notesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  }

  const removeItinerary = (removeId, notes: AccommodationInternalNote[]) => {
    const values = Object.values(notes);
    return values.filter(obj => obj.id !== removeId);
  };

  const removeItineraryPublic = (
    removeId,
    notes: AccommodationDetailsPublicNotes[],
  ) => {
    const values = Object.values(notes);
    return values.filter(obj => obj.id !== removeId);
  };

  const removeInternalNote = event => {
    const { target } = event;
    const internalNotes = { ...accommodationNotesState.internalNotes };
    const internalNotesTemp = removeItinerary(target.id, internalNotes);
    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.internalNotes = internalNotesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  };
  const removePublicNote = event => {
    const { target } = event;
    const publicNotes = { ...accommodationNotesState.publicNotes };
    const publicNotesTemp = removeItineraryPublic(target.id, publicNotes);
    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.publicNotes = publicNotesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  };
  const editNotes = (notes, noteKey, noteText, id) => {
    if (notes.length <= 0) {
      return null;
    }
    notes = notes.map(item => {
      const temp = item;
      if (temp.noteKey === noteKey && (temp.id === id || !temp.id)) {
        item = { ...temp, noteText };
      }
      return item;
    });
    return notes;
  };

  const editNotesPublic = (notes, noteKey, noteText, id) => {
    if (notes.length <= 0) {
      return null;
    }
    notes = notes.map(item => {
      const temp = item;
      if (temp.id === id || !temp.id) {
        item = { ...temp, noteText };
      }
      return item;
    });
    return notes;
  };

  const editNotesPublicDateStart = (notes, noteKey, value, id) => {
    if (notes.length <= 0) {
      return null;
    }

    notes = notes.map(item => {
      const temp = item;
      if (temp.id === id || !temp.id) {
        temp.visibilityStartsAt = value;
      }
      return temp;
    });
    return notes;
  };

  const editNotesPublicDateEnd = (notes, noteKey, value, id) => {
    if (notes.length <= 0) {
      return null;
    }
    notes = notes.map(item => {
      const temp = item;
      if (temp.id === id || !temp.id) {
        temp.visibilityEndsAt = value;
      }
      return temp;
    });
    return notes;
  };

  const editTranslations = (translations, keyId, locale, value) => {
    let exist = false;

    translations = translations.map(item => {
      const temp = item;
      if (temp.keyId === keyId && temp.locale === locale) {
        const temp2 = { ...temp, text: value };
        exist = true;
        return temp2;
      }
      return item;
    });
    if (!exist) {
      const newitem: AccommodationDetailsTranslations = {
        id: null,
        locale,
        keyId,
        text: value,
        isDeleted: false,
      };
      translations = [...translations, newitem];
      return translations;
    }
    return translations;
  };

  const handleInputChangeNotes = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let notesTemp = accommodationNotesState.internalNotes;
    notesTemp = editNotes(notesTemp, name, value, target.id);

    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.internalNotes = notesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  };
  const handleInputChangeAddress = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let addressTemp = addressDetailsTemp;
    addressTemp = { ...addressTemp, [name]: value };
    setAddress(addressTemp);
  };

  const handleInputChangeAccountIndex = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let accountTemp = editedAccount;
    accountTemp = { ...accountTemp, index: value };
    setEditedAccount(accountTemp);
  };

  const [imagesList, setImagesList] = useState<AccommodationImageType[]>([]);

  const debouncedSetImageList = useCallback(
    debounce(() => {
      // Your existing function body
      if (accommodationImages && accommodationImages.length > 0) {
        const images = accommodationImages
          .filter(
            ima =>
              ima !== null &&
              ima !== undefined &&
              ima.fullUrl !== null &&
              ima.fullUrl !== undefined &&
              !ima.fullUrl!.includes('/thumb') &&
              !ima.fullUrl!.includes('/carou') &&
              !ima.fullUrl!.includes('/previ'),
          )
          .map(
            item =>
              ({
                fileName: item.fileName,
                fullUrl: item.fullUrl,
                description: item.description,
                index: item.index,
                id: item.id,
                modifiedDate: item.modifiedDate,
                altText: item.altText,
                descriptionEn: item.descriptionEn,
                grediFileId: item.grediFileId,
                descriptionRu: item.descriptionRu,
              }) as AccommodationImageType,
          );
        setImagesList(images);
      }
    }, 1000),
    [accommodationImages],
  );

  // function setImageList() {
  //   if (accommodationImages.length > 0) {
  //     const images = accommodationImages
  //       .filter(
  //         ima =>
  //           ima !== null &&
  //           ima !== undefined &&
  //           ima.fullUrl !== null &&
  //           ima.fullUrl !== undefined &&
  //           !ima.fullUrl!.includes('/thumb') &&
  //           !ima.fullUrl!.includes('/carou') &&
  //           !ima.fullUrl!.includes('/previ'),
  //       )
  //       .map(
  //         item =>
  //           ({
  //             fileName: item.fileName,
  //             fullUrl: item.fullUrl,
  //             description: item.description,
  //             index: item.index,
  //             id: item.id,
  //             modifiedDate: item.modifiedDate,
  //             altText: item.altText,
  //             descriptionEn: item.descriptionEn,
  //             grediFileId: item.grediFileId,
  //             descriptionRu: item.descriptionRu,
  //           }) as AccommodationImageType,
  //       );
  //     setImagesList(images);
  //   }
  // }
  const handleInputChangePublicNotes = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let pupNotesTemp = accommodationNotesState.publicNotes;
    pupNotesTemp = editNotesPublic(pupNotesTemp, name, value, target.id);

    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.publicNotes = pupNotesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  };

  const handleDateValueStart = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let notesTemp = accommodationNotesState.publicNotes;
    notesTemp = editNotesPublicDateStart(notesTemp, name, value, target.id);

    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.publicNotes = notesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  };

  const handleDateValueEnd = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    let notesTemp = accommodationNotesState.publicNotes;
    notesTemp = editNotesPublicDateEnd(notesTemp, name, value, target.id);

    const storeAccommodationNotes = { ...accommodationNotesState };
    storeAccommodationNotes.publicNotes = notesTemp;
    setAccommodationNotesState(storeAccommodationNotes);
  };

  const handleInputChangeTranslationV2 = (
    name: string,
    id: string,
    value: string,
  ) => {
    let translationsTemp = accommodationBasicTemp.translations;
    if (id.endsWith('FiFi')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'fi_FI',
        value,
      );
    } else if (id.endsWith('EnEn')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'en_EN',
        value,
      );
    } else if (id.endsWith('RuRu')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'ru_RU',
        value,
      );
    }

    const storeAccommodation = { ...accommodationBasicTemp };
    storeAccommodation.translations = translationsTemp;
    setAccommodationBasic(storeAccommodation);
  };

  const handleInputChangeTranslation = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { id } = target;
    const { name } = target;
    let translationsTemp = accommodationBasicTemp.translations;
    if (id.endsWith('FiFi')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'fi_FI',
        value,
      );
    } else if (id.endsWith('EnEn')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'en_EN',
        value,
      );
    } else if (id.endsWith('RuRu')) {
      translationsTemp = editTranslations(
        translationsTemp,
        name,
        'ru_RU',
        value,
      );
    }

    const storeAccommodation = { ...accommodationBasicTemp };
    storeAccommodation.translations = translationsTemp;
    setAccommodationBasic(storeAccommodation);
  };

  const renderHistoryValues = (values: unknown) => {
    if (!values) return null;
    const isObject = (v: unknown): v is Record<string, unknown> =>
      typeof v === 'object' && v !== null && !Array.isArray(v);

    if (!isObject(values)) {
      // Fallback for non-object shapes
      return <span className="text-break">{JSON.stringify(values)}</span>;
    }

    const entries = Object.entries(values as Record<string, unknown>);
    if (entries.length === 0) return null;

    return (
      <div className="text-break" style={{ maxWidth: 400 }}>
        {entries.map(([compositeKey, inner]) => {
          const [type, id] = String(compositeKey).split(':');
          const innerIsObj = isObject(inner);
          const innerEntries = innerIsObj
            ? Object.entries(inner as Record<string, unknown>).filter(
                ([, v]) => v !== null && v !== undefined && v !== '',
              )
            : [];

          if (!innerIsObj || innerEntries.length === 0) return null;

          return (
            <div key={compositeKey} className="mb-8">
              <div className="fw-bold">
                {type}
                {id ? ` (${id})` : ''}
              </div>
              <ul className="mb-0 ps-16">
                {innerEntries.map(([field, val]) => (
                  <li key={field}>
                    <span className="text-muted">{field}: </span>
                    <span>{String(val)}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  const accommodationLogItems = accommodationLogs?.data?.map(item => (
    <tr key={Math.random()}>
      <td className="p-16">{formatUtcDateTimeToFinnish(item.timestamp)}</td>
      <td className="p-16">
        {(item?.authorFirstName || item?.authorLastName) && (
          <div>
            {`${item?.authorFirstName || ''} ${item?.authorLastName || ''}`.trim()}
          </div>
        )}
        {/* <div>Account ID: {item?.accountId || ''}</div> */}
      </td>
      <td className="p-16">
        {item?.targetName && <div>{item.targetName}</div>}
        {/* Kohde ID:{item?.targetId && <div>{item.targetId}</div>} */}
      </td>
      <td className="p-16">
        {item?.eventType && <div>{item.eventType}</div>}
        {/* Kohde ID:{item?.targetId && <div>{item.targetId}</div>} */}
      </td>
      <td className="p-16">{renderHistoryValues(item?.oldValues)}</td>
      <td className="p-16">{renderHistoryValues(item?.newValues)}</td>
    </tr>
  ));

  const accommodationAddOnItems =
    accommodationAddOnsState?.addOns?.length > 0
      ? accommodationAddOnsState?.addOns
          ?.filter(i => i.id !== '' && i.id !== null && i.id !== undefined)
          .map((item, index) => (
            <tr key={item.id || `addon-${index}`}>
              <td>
                {item.addOnNumber ?? ''} {item.name}
              </td>
              <td>
                {item.isIncludedInPrice && (
                  <div className="text-danger">Sisältyy hintaan</div>
                )}
                {!item.isIncludedInPrice && (
                  <div className="text-black">Ei Sisällly hintaan</div>
                )}
              </td>
              {item.automatic === true ? (
                <td className="text-info">Näkyy varattavissa</td>
              ) : (
                <td className="text-danger">Ei Näy varattavissa</td>
              )}
              <td>{item.price}</td>
              <td>{item.purchasePrice}</td>
              <td style={{ width: 100 }}>
                <Button
                  onClick={() => handleShowEditAddon(item, item.id)}
                  disabled={!accommodationBasicTemp.isAllowedToEdit}
                  variant="outline-blue"
                  size="sm"
                >
                  Muokkaa
                </Button>
              </td>
              <td style={{ width: 48 }}>
                <Button
                  onClick={() => deleteAddOn(item)}
                  disabled={!accommodationBasicTemp.isAllowedToEdit}
                  size="sm"
                  variant="outline-danger"
                >
                  <span className="bi bi-trash-fill">
                    <span className="visually-hidden">Poista</span>
                  </span>
                </Button>
              </td>
            </tr>
          ))
      : null;
  const buildingItems = accommodationBuildingsTemp?.map(item => (
    <tr key={Math.random()}>
      <td>{getItem('fi_FI', 'BuildingName', item)}</td>
      <td style={{ width: 100 }}>
        <Button
          onClick={() => editBuilding(item)}
          size="sm"
          disabled={!accommodationBasicTemp.isAllowedToEdit}
          variant="outline-blue"
        >
          Muokkaa
        </Button>
      </td>
      <td style={{ width: 48 }}>
        <Button
          onClick={() => deleteBuilding(item.id)}
          disabled={!accommodationBasicTemp.isAllowedToEdit}
          size="sm"
          variant="outline-danger"
        >
          <span className="bi bi-trash-fill">
            <span className="visually-hidden">Poista</span>
          </span>
        </Button>
      </td>
    </tr>
  ));

  const GetAttributeModalSelectOptions: React.JSX.Element[] | null = [
    <option key="not-selected" value="">
      Ei valittu
    </option>,
    ...(accommodationAttributes?.map((item: AccommodationAttributeListItem) => (
      <option key={item.id} value={item.id}>
        {item.name}
      </option>
    )) || []),
  ];
  const GetSelectNewAttributeModal = (
    <Modal
      show={showSelectNew}
      onHide={handleCloseSelectNew}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Valitse uusi ominaisuus</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AccommodationInputSelect
          Disabled={!accommodationBasicTemp.isAllowedToEdit}
          HandleChange={handleSelectNewAttribute}
          LabelText="Valittavissa olevat ominaisuudet"
          SelectId="attributeModalOptionsSelect"
        >
          {GetAttributeModalSelectOptions}
        </AccommodationInputSelect>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={handleCloseSelectNew}>
          Peruuta ja sulje
        </Button>
        {/* <Button variant="primary" onClick={saveNewAttribute}> */}
        {/*  Tallenna */}
        {/* </Button> */}
      </Modal.Footer>
    </Modal>
  );

  const accommodationMokinTiedotAttributeItems =
    accommodationAttributesState?.accommodationAttributes
      .filter(ai => getAttributeIsMokinTiedot(ai.attributeId) === true)
      .map((item, index) => (
        <tr key={item.id || Math.random()} className="position-relative">
          <td>{getAttributeItem(item.attributeId)}</td>
          <td>
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'true' && (
                <span className="bi bi-check-lg text-success me-4">
                  <span className="visually-hidden">Kyllä</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'false' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === '' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) !== '' && (
                <span>{getAttributeItemValue(item.attributeId, item)}</span>
              )}
          </td>
          <td style={{ width: 100 }}>
            {getAttributeItemType(item.attributeId) === 'NumericPlain' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericPlain(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'FreeText' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowFreeText(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMin' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMin(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'MultiSelect' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowMultiSelect(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'Boolean' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowBoolean(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMax' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMax(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
          </td>
          <td style={{ width: 48 }}>
            <Button
              onClick={() => deleteAccommodationAttribute(item)}
              disabled={!accommodationBasicTemp.isAllowedToEdit}
              size="sm"
              variant="outline-danger"
              className="px-16"
            >
              <span className="bi bi-trash-fill">
                <span className="visually-hidden">Poista</span>
              </span>
            </Button>
          </td>
        </tr>
      ));

  const accommodationDistancesAttributeItems =
    accommodationAttributesState?.accommodationAttributes
      .filter(ai => getAttributeIsDistances(ai.attributeId) === true)
      .map((item, index) => (
        <tr key={item.id || Math.random()} className="position-relative">
          <td>{getAttributeItem(item.attributeId)}</td>
          <td>
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'true' && (
                <span className="bi bi-check-lg text-success me-4">
                  <span className="visually-hidden">Kyllä</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'false' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === '' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) !== '' && (
                <span>
                  {getAttributeItemValue(item.attributeId, item)}
                  {getAttributeIsDistances(item.attributeId) && (
                    <span> km</span>
                  )}
                </span>
              )}
          </td>
          <td style={{ width: 100 }}>
            {getAttributeItemType(item.attributeId) === 'NumericPlain' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericPlain(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'FreeText' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowFreeText(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMin' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMin(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'MultiSelect' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowMultiSelect(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'Boolean' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowBoolean(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMax' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMax(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
          </td>
          <td style={{ width: 48 }}>
            <Button
              onClick={() => deleteAccommodationAttribute(item)}
              size="sm"
              disabled={!accommodationBasicTemp.isAllowedToEdit}
              variant="outline-danger"
              className="px-16"
            >
              <span className="bi bi-trash-fill">
                <span className="visually-hidden">Poista</span>
              </span>
            </Button>
          </td>
        </tr>
      ));

  const accommodationOtherAttributeItems =
    accommodationAttributesState?.accommodationAttributes
      .filter(ai => getAttributeIsOther(ai.attributeId) === true)
      .map((item, index) => (
        <tr
          key={item.attributeId || Math.random()}
          className="position-relative"
        >
          <td>{getAttributeItem(item.attributeId)}</td>
          <td>
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'true' && (
                <span className="bi bi-check-lg text-success me-4">
                  <span className="visually-hidden">Kyllä</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'false' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === '' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) !== '' && (
                <span>{getAttributeItemValue(item.attributeId, item)}</span>
              )}
          </td>
          <td style={{ width: 100 }}>
            {getAttributeItemType(item.attributeId) === 'NumericPlain' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericPlain(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'FreeText' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowFreeText(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMin' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMin(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'MultiSelect' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowMultiSelect(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'Boolean' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowBoolean(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMax' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMax(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
          </td>
          <td style={{ width: 48 }}>
            <Button
              onClick={() => deleteAccommodationAttribute(item)}
              size="sm"
              variant="outline-danger"
              disabled={!accommodationBasicTemp.isAllowedToEdit}
              className="px-16"
            >
              <span className="bi bi-trash-fill">
                <span className="visually-hidden">Poista</span>
              </span>
            </Button>
          </td>
        </tr>
      ));

  const accommodationVarusteluAttributeItems =
    accommodationAttributesState?.accommodationAttributes
      .filter(ai => getAttributeIsVarustelu(ai.attributeId) === true)
      .map((item, index) => (
        <tr key={item.id || Math.random()} className="position-relative">
          <td>{getAttributeItem(item.attributeId)}</td>
          <td>
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'true' && (
                <span className="bi bi-check-lg text-success me-4">
                  <span className="visually-hidden">Kyllä</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) === 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === 'false' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) === '' && (
                <span className="bi bi-x-lg text-danger me-4">
                  <span className="visually-hidden">Ei</span>
                </span>
              )}
            {getAttributeItemType(item.attributeId) !== 'Boolean' &&
              getAttributeItemValue(item.attributeId, item) !== '' && (
                <span>{getAttributeItemValue(item.attributeId, item)}</span>
              )}
          </td>
          <td style={{ width: 100 }}>
            {getAttributeItemType(item.attributeId) === 'NumericPlain' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericPlain(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'FreeText' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowFreeText(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMin' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMin(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'MultiSelect' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowMultiSelect(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'Boolean' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowBoolean(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
            {getAttributeItemType(item.attributeId) === 'NumericMax' && (
              <Button
                variant="outline-blue"
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                size="sm"
                onClick={() => handleShowNumericMax(item, item.attributeId)}
              >
                Muokkaa
              </Button>
            )}
          </td>
          <td style={{ width: 48 }}>
            <Button
              onClick={() => deleteAccommodationAttribute(item)}
              size="sm"
              variant="outline-danger"
              disabled={!accommodationBasicTemp.isAllowedToEdit}
              className="px-16"
            >
              <span className="bi bi-trash-fill">
                <span className="visually-hidden">Poista</span>
              </span>
            </Button>
          </td>
        </tr>
      ));
  const accommodationGroupsList = accommodationGroups.map(group => {
    return { value: group.id, label: group.name };
  });
  const travelAgencyDomainsList = travelAgencyDomains.map(partner => {
    return { value: partner.id, label: partner.partnerName };
  });
  const travelAgencyAccountsList = travelAgencyAccounts.map(account => {
    return {
      value: account.id.toLowerCase(),
      label: `${account.firstName} ${account.lastName}`,
    };
  });
  const handleCancelNewAttribute = event => {
    const filteredAccommodationAttributes =
      accommodationAttributesState.accommodationAttributes.filter(
        item => item.id !== undefined && item.id !== null,
      );
    const storeState = {
      ...accommodationAttributesState,
      accommodationAttributes: filteredAccommodationAttributes,
    };
    setAccommodationAttributesState(storeState);
    setShowMultiSelect(false);
    setShowBoolean(false);
    setShowFreeText(false);
    setShowNumericMax(false);
    setShowNumericMin(false);
    setShowNumericPlain(false);
  };
  const HandleItemReorder = (OldNumber: number, NewNumber: number) => {
    try {
      if (
        OldNumber === undefined ||
        NewNumber === undefined ||
        OldNumber === null ||
        NewNumber === null
      ) {
        console.warn('HandleItemReorder called with invalid indices', {
          OldNumber,
          NewNumber,
        });
        return;
      }

      const from = Math.max(0, Math.min(OldNumber - 1, imagesList.length - 1));
      const to = Math.max(0, Math.min(NewNumber - 1, imagesList.length - 1));
      if (from === to) {
        return;
      }
      // Pure immutable reorder: remove at `from`, insert at `to`
      const working = [...imagesList];
      const [moved] = working.splice(from, 1);
      working.splice(to, 0, moved);

      // Normalize indices to mirror array order (1..n)
      const normalized: AccommodationImageType[] = working.map((item, i) => ({
        ...item,
        index: i + 1,
      }));

      // Defensive validation: indices unique and continuous
      const set = new Set(normalized.map(x => x.index));
      if (set.size !== normalized.length) {
        console.warn('Index normalization fixed duplicates', {
          before: imagesList.map(x => x.index),
          after: normalized.map(x => x.index),
        });
      }
      const updateDataImages = normalized.map(
        item =>
          ({
            fullUrl: item.fullUrl,
            description: item.description,
            index: item.index,
            descriptionRu: item.descriptionRu,
            fileName: item.fileName,
            grediFileId: item.grediFileId,
            altText: item.altText,
            descriptionEn: item.descriptionEn,
            modifiedDate: item.modifiedDate,
            id: item.id,
          }) as AccommodationImage,
      );

      const accommodationUpdateData = {
        ImageIndexes: updateDataImages,
        accommodationId: params.accommodationId,
      } as AccommodationUpdateImages;
      // Optimistic UI update
      setImagesList(normalized);

      dispatch(
        updateAccommodationImageIndexesAsync(accommodationUpdateData),
      ).then(() => {
        dispatch(
          addStatusToast({
            Message: `Kuvien järjestys tallennettu`,
          }),
        );
      });
    } catch (err) {
      console.error('HandleItemReorder failed', err);
    }
  };

  const updateImages = (imageList: AccommodationImageType[]) => {
    const updateDataImages = imageList.map(
      item =>
        ({
          fullUrl: item.fullUrl,
          description: item.description,
          index: item.index,
          descriptionRu: item.descriptionRu,
          fileName: item.fileName,
          grediFileId: item.grediFileId,
          altText: item.altText,
          descriptionEn: item.descriptionEn,
          modifiedDate: item.modifiedDate,
          id: item.id,
        }) as AccommodationImage,
    );
    const accommodationUpdateData = {
      ImageIndexes: updateDataImages,
      accommodationId: params.accommodationId,
    } as AccommodationUpdateImages;
    dispatch(updateAccommodationImageIndexesAsync(accommodationUpdateData))
      .then(res => {
        dispatch(getAccommodationImagesAsync(params.accommodationId));
      })
      .then(() => {
        dispatch(
          addStatusToast({
            Message: `Kuvien järjestys tallennettu`,
          }),
        );
      });
    setImagesList(imageList);
  };

  const accommodationNotesItems = accommodationNotesState.internalNotes?.map(
    item => (
      <tr key={item.id || Math.random()}>
        <td className="py-16">
          <textarea
            className="form-control"
            rows={6}
            id={item.id}
            value={item.noteText}
            onChange={handleInputChangeNotes}
            name="AccommodationInternalInformation"
          />
        </td>
        <td style={{ width: 48 }} className="py-16">
          <Button
            onClick={event => removeInternalNote(event)}
            id={item.id}
            name="removeButton"
            variant="outline-danger"
            size="sm"
          >
            <span className="bi bi-trash-fill">
              <span className="visually-hidden">Poista</span>
            </span>
          </Button>
        </td>
      </tr>
    ),
  );

  const accommodationPublicNotesItems =
    accommodationNotesState.publicNotes?.map(item => (
      <tr key={item.id || Math.random()}>
        <td className="py-16">
          <textarea
            className="form-control"
            rows={7}
            id={item.id}
            value={item.noteText}
            onChange={handleInputChangePublicNotes}
            name="AccommodationPublicInformation"
          />
        </td>
        <td className="py-16">
          <Form.Group className="mb-8">
            <Form.Label htmlFor={item.id}>Alkaa</Form.Label>
            <input
              type="datetime-local"
              id={item.id}
              required
              value={item.visibilityStartsAt || ''}
              onChange={handleDateValueStart}
              name="visibilityStartsAt"
              className="form-control"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor={item.id}>Päättyy</Form.Label>
            <input
              type="datetime-local"
              id={item.id}
              required
              value={item.visibilityEndsAt || ''}
              onChange={handleDateValueEnd}
              name="visibilityEndsAt"
              className="form-control"
            />
          </Form.Group>
        </td>
        <td style={{ width: 48 }} className="py-16">
          <Button
            onClick={event => removePublicNote(event)}
            id={item.id}
            name="removeButton"
            variant="outline-danger"
            size="sm"
          >
            <span className="bi bi-trash-fill">
              <span className="visually-hidden">Poista</span>
            </span>
          </Button>
        </td>
      </tr>
    ));
  const [accountingFilter, setAccountingFilter] = useState(
    initialAccountingFilter,
  );
  const initFetch = useCallback(() => {
    setAccommodationBasic(initialAccommodationDetailsBasic);
    setAccommodationNotesState(initialAccommodationNotes);
    setAccommodationAttributesState(initialAccommodationAttributes);
    setAccommodationAddOnsState(initialAccommodationAddOns);
    getSeasonRates(params.accommodationId);
    dispatch(getAccommodationAttributesAsync(params.accommodationId));
    dispatch(getAccommodationDetailsBasicAsync(params.accommodationId));
    dispatch(getAccommodationAccountsAsync(params.accommodationId));
    dispatch(
      getAllRolesAsync({
        pageNumber: 1,
        pageSize: 10,
        culture: 'fi_FI',
        searchString: '',
      }),
    );

    dispatch(getAccommodationDetailsBasicAsync(params.accommodationId)).then(
      detailsResult => {
        if (getAccommodationDetailsBasicAsync.fulfilled.match(detailsResult)) {
          const details = detailsResult.payload;
          setAccommodationBasic(prev => ({ ...prev, ...details }));
          dispatch(
            getAccommodationCapacitiesAsync(params.accommodationId),
          ).then(capacitiesResult => {
            if (
              getAccommodationCapacitiesAsync.fulfilled.match(capacitiesResult)
            ) {
              setAccommodationBasic(prev => ({
                ...prev,
                capacityIds:
                  capacitiesResult.payload !== null &&
                  capacitiesResult.payload.capacityIds !== null
                    ? capacitiesResult.payload.capacityIds
                    : [],
              }));
            }
          });
        }
      },
    );
    filtering.AggregateId = params.accommodationId;
    filteringRoomsAttr.id = params.accommodationId;
    setFiltering(filtering);
    setFilteringRoomsAttr(filteringRoomsAttr);
    setRoomsFiltering(roomsFiltering);
    setImagesList([]);
    dispatch(
      getCountriesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
        culture: LOCALES.Fi,
      }),
    );
    dispatch(
      getRegionsAsync({ PageNumber: 1, PageSize: 100000, searchString: '' }),
    );
    dispatch(
      getMunicipalitiesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
      }),
    );
    dispatch(
      getSkiResortsAsync({ PageNumber: 1, PageSize: 100000, searchString: '' }),
    );
    dispatch(
      getPlacesAsync({ PageNumber: 1, PageSize: 100000, searchString: '' }),
    );
    // dispatch(getAccommodationLogsAsync(filtering));
    dispatch(getAllAccommodationGroupsAsync(filteringGroup));
    // dispatch(getAllAccommodationAttributesAsync(filteringAttr));
    // dispatch(getAllAccommodationAttributesAsync(filteringAttr));
    dispatch(getAllPurchaseVatsAsync(accountingFilter));
  }, [
    addOnsFiltering,
    dispatch,
    filtering,
    filteringAttr,
    filteringGroup,
    filteringRoomsAttr,
    accountingFilter,
    params.accommodationId,
  ]);
  useEffect(() => {
    // Fetch initial data based on the active tab
    setActiveTab(activeTab);
  }, [activeTab]);
  useEffect(() => {
    if (location.state != null && location.state.tab != null) {
      setActiveTab(location.state.tab);
    } else if (query.get('tab')) {
      setActiveTab(query.get('tab'));
    }
  }, [location.state, query]);
  useEffect(() => {}, [editedAttributeItemData]);
  useEffect(() => {}, [accommodationLogs]);
  useEffect(() => {
    if (buildings !== undefined && buildings !== null && buildings.length > 0) {
      if (editedBuilding && editedBuilding.id) {
        const found = buildings.find(b => b.id === editedBuilding.id);
        if (found) {
          setEditedBuilding(found);
        } else {
          setEditedBuilding(buildings[0]);
        }
      } else {
        setEditedBuilding(buildings[0]);
      }
      setAccommodationBuildingsTemp(buildings);
    }
  }, [buildings]);
  useEffect(() => {}, [
    accommodationAttributesState.accommodationAttributes.length,
  ]);
  useEffect(() => {
    if (
      accommodationsAttributes != null &&
      accommodationsAttributes.accommodationAttributes != null &&
      accommodationsAttributes.accommodationAttributes.length > 0
    ) {
      setAccommodationAttributesState(accommodationsAttributes);
    }
  }, [accommodationsAttributes]);
  useEffect(() => {
    if (
      accommodationAddOnList !== null &&
      accommodationAddOnList !== undefined &&
      accommodationAddOnList.addOns.length > 0
    ) {
      setAccommodationAddOnsState(accommodationAddOnList);
    } else {
      setAccommodationAddOnsState({
        id: '',
        addOns: [],
      } as AccommodationAddOns);
    }
  }, [accommodationAddOnList]);
  useEffect(() => {
    if (newAttributeOptions !== null && newAttributeOptions.length > 0) {
      const storedata = {
        ...editedAttributeItemData,
        multiSelectOptions: newAttributeOptions,
      };
      seteditedAttributeItemData(storedata);
    }
  }, [newAttributeOptions]);
  useEffect(() => {}, [transferring]);
  useEffect(() => {}, [error]);
  useEffect(() => {
    if (accommodationNotes != null && accommodationNotes.id != null) {
      setAccommodationNotesState(accommodationNotes);
    }
  }, [accommodationNotes]);
  useLayoutEffect(() => {
    debouncedSetImageList();
  }, [accommodationImages]);

  useEffect(() => {
    if (
      allAccountList !== null &&
      allAccountList.length > 0 &&
      accommodationAccountList !== null &&
      accommodationAccountList.length > 0
    ) {
      setAllAccountsForModal([]);
      let added = allAccountList;
      added = added.concat(accommodationAccountList);
      const foo = new Map();
      for (let i = 0; i < added.length; i += 1) {
        const tag = added[i];
        foo.set(tag.id, tag);
      }
      const final = [...foo.values()];
      setAllAccountsForModal(final);
      setNewAccountSelectDisabled(false);
    } else if (
      allAccountList !== null &&
      allAccountList.length > 0 &&
      originalAccommodationAccountList !== null &&
      originalAccommodationAccountList.length > 0
    ) {
      setAllAccountsForModal([]);
      let added = allAccountList;
      added = added.concat(originalAccommodationAccountList);
      const foo = new Map();
      for (let i = 0; i < added.length; i += 1) {
        const tag = added[i];
        foo.set(tag.id, tag);
      }
      const final = [...foo.values()];
      setAllAccountsForModal(final);
      setNewAccountSelectDisabled(false);
    } else if (
      accommodationAccountList !== null &&
      accommodationAccountList.length > 0
    ) {
      setAllAccountsForModal(accommodationAccountList);
      setNewAccountSelectDisabled(false);
    } else if (allAccountList !== null && allAccountList.length > 0) {
      setAllAccountsForModal([]);
      const added = allAccountList;
      const foo = new Map();
      for (let i = 0; i < added.length; i += 1) {
        const tag = added[i];
        foo.set(tag.id, tag);
      }
      const final = [...foo.values()];
      setAllAccountsForModal(final);
      setNewAccountSelectDisabled(false);
    }
  }, [allAccountList, originalAccommodationAccountList]);
  useEffect(() => {}, [allAccountsForModal]);
  useEffect(() => {}, [accountRoles]);
  useEffect(() => {
    if (buildingRooms !== null) {
      setAttributeRoomsTemp(buildingRooms);
    }
  }, [buildingRooms]);
  useEffect(() => {}, [allRooms]);
  useEffect(() => {}, [allBeds]);
  useEffect(() => {}, [allRoomAreas]);
  useEffect(() => {}, [allBuildings]);
  useEffect(() => {}, [allFloors]);
  useEffect(() => {
    if (accountRoles !== null) {
      setAllowRoleSelect(true);
    }
  }, [accountRoles]);
  useEffect(() => {}, [accommodationBasicTemp]);
  useEffect(() => {}, [userRolesOptionsOptions]);
  useEffect(() => {
    if (buildingRoomAreas !== null) {
      setAttributeRoomareasTemp(buildingRoomAreas);
    }
  }, [buildingRoomAreas]);
  useEffect(() => {
    initFetch();
  }, [initFetch]);
  useEffect(() => {
    if (
      accommodationDetailsBasic !== null &&
      accommodationDetailsBasic.addressId !== null &&
      accommodationDetailsBasic.addressId.length > 0
    ) {
      if (
        addressDetails == null &&
        accommodationDetailsBasic.addressId != null
      ) {
        dispatch(
          getIdentityAddressDetailsAsync(accommodationDetailsBasic.addressId),
        );
      } else if (addressDetails.id !== accommodationDetailsBasic.addressId) {
        dispatch(
          getIdentityAddressDetailsAsync(accommodationDetailsBasic.addressId),
        );
      } else {
        setAddress(addressDetails);
      }
    } else {
      setAddress({
        id: null,
        postStreetAddress: '',
        postCity: '',
        postCode: '',
        isPrimary: false,
        description: '',
        additionalAddressLine: '',
        countryId: '',
      } as AddressResponse);
    }
  }, [addressDetails, accommodationDetailsBasic, dispatch]);

  useEffect(() => {
    if (
      accommodationDetailsBasic &&
      accommodationDetailsBasic.id.toUpperCase() ===
        params.accommodationId.toUpperCase()
    ) {
      setAccommodationBasic(accommodationDetailsBasic);
    } else {
      setAccommodationBasic(initialAccommodationDetailsBasic);
    }
  }, [accommodationDetailsBasic, params.accommodationId]);
  const OkAddAccount = () => {
    if (
      !editedAccount.accountId ||
      !editedAccount.roleId ||
      editedAccount.accountId === '00000000-0000-0000-0000-000000000000' ||
      editedAccount.roleId === '00000000-0000-0000-0000-000000000000'
    ) {
      setErrorText('Molemmat, sekä Omistaja ja Rooli on valittava');
      return;
    }
    setErrorText(null);

    let ownersTemp = [...accommodationAccountList];
    const itemIndex = ownersTemp.findIndex(
      o => o.roleId === editedAccount.roleId && o.id === editedAccount.id,
    );
    if (itemIndex > -1) {
      const edited = ownersTemp[itemIndex];
      ownersTemp[itemIndex] = {
        ...edited,
        id: editedAccount.id,
        accountId: editedAccount.accountId,
        roleId: editedAccount.roleId,
        index: editedAccount.index,
      };
    } else {
      const edited = {
        id: editedAccount.id,
        roleId: editedAccount.roleId,
        accountId: editedAccount.accountId,
        index: editedAccount.index,
      } as AccountListItem;
      ownersTemp = [...ownersTemp, edited];
    }
    setNewAccountSelectDisabled(false);
    setIsAddAccountModalVisible(false);
    const updateData = {
      id: accommodationDetailsBasic.id,
      updateAccommodationAccountRoleCollection: ownersTemp.map(
        item =>
          ({
            accountId: item.accountId,
            roleId: item.roleId,
            index: item.index,
            id: item.id,
          }) as AccommodationAccount,
      ),
    } as AccommodationAccountsUpdate;
    dispatch(updateAccommodationAccountsAsync(updateData)).then(res => {
      dispatch(getAccommodationAccountsAsync(accommodationDetailsBasic.id));
    });
  };
  const OkClearAddedAccounts = () => {
    setErrorText(null);
    setIsAddAccountModalVisible(false);
    setNewAccountSelectDisabled(false);
    setIsAddAccountModalVisible(false);
    const updateData = {
      id: accommodationDetailsBasic.id,
      updateAccommodationAccountRoleCollection: accommodationAccountList
        .filter(ar => ar.id !== 'new')
        .map(
          item =>
            ({
              accountId: item.accountId,
              roleId: item.roleId,
              index: item.index,
              id: item.id,
            }) as AccommodationAccount,
        ),
    } as AccommodationAccountsUpdate;
    dispatch(updateAccommodationAccountsAsync(updateData)).then(res => {
      dispatch(getAccommodationAccountsAsync(accommodationDetailsBasic.id));
    });
  };

  const updateAccounts = () => {
    const ownersTemp = [...accommodationAccountList];
    const updateData = {
      id: accommodationDetailsBasic.id,
      updateAccommodationAccountRoleCollection: ownersTemp.map(
        item =>
          ({
            accountId: item.accountId,
            roleId: item.roleId,
            index: item.index,
            id: item.id,
          }) as AccommodationAccount,
      ),
    } as AccommodationAccountsUpdate;
    dispatch(updateAccommodationAccountsAsync(updateData)).then(res => {
      dispatch(getAccommodationAccountsAsync(accommodationDetailsBasic.id));
    });
  };

  const updateAccommodationRemoveCrossell = () => {
    if (activeTab === 'Mökin perustiedot') {
      // Navigate immediately before performing any other actions
      navigate('/?triggerPlainSearch=true', {
        replace: true,
      });

      const updateAccommodationdata = getUpdateDataCross(
        accommodationBasicTemp,
      );
      dispatch(updateAccommodatioBasicAsync(updateAccommodationdata));
    }
  };

  // Sportum capacities
  const capacityIdsTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const updateAccommodation = () => {
    let updateOk = true;
    if (activeTab === 'Mökin perustiedot') {
      const updateAccommodationdata = getUpdateData(accommodationBasicTemp);
      dispatch(updateAccommodatioBasicAsync(updateAccommodationdata)).then(
        result => {
          if (updateAccommodatioBasicAsync.fulfilled.match(result)) {
            setIsSyncingBasic(true);
            // Save Sportum capacity ids too (only for capacity products)
            if (accommodationBasicTemp?.isCapacityProduct) {
              const capacityIdsRaw =
                capacityIdsTextAreaRef.current?.value ?? '';
              const capacityIds = capacityIdsRaw
                .split(/\r?\n|,/)
                .map(id => id.trim())
                .filter(id => id !== '');

              const command: SetCapacitiesCommand = {
                accommodationId: accommodationBasicTemp.id,
                capacitiesIdDataDto: {
                  capacityIds,
                  capacityType: '1',
                  parentId: accommodationBasicTemp.sportumId,
                },
              };
              dispatch(setAccommodationCapacitiesAsync(command)).then(
                capacityResult => {
                  // Refetch capacities after saving
                  if (
                    setAccommodationCapacitiesAsync.fulfilled.match(
                      capacityResult,
                    )
                  ) {
                    dispatch(
                      getAccommodationCapacitiesAsync(
                        accommodationBasicTemp.id,
                      ),
                    ).then(fetchResult => {
                      if (
                        getAccommodationCapacitiesAsync.fulfilled.match(
                          fetchResult,
                        )
                      ) {
                        // Update accommodationBasicTemp with the fresh data
                        setAccommodationBasic(prevState => ({
                          ...prevState,
                          capacityIds: fetchResult.payload.capacityIds || [],
                        }));
                      } else {
                        updateOk = false;
                        dispatch(
                          addStatusToast({
                            Message: `Virhe kohteen tietojen päivittämisessä.`,
                          }),
                        );
                      }
                    });
                  }
                },
              );
            }
          }

          dispatch(
            getAccommodationDetailsBasicAsync(params.accommodationId),
          ).then(res2 => {
            if (res2.meta.requestStatus === 'fulfilled') {
              setAccommodationBasic(res2.payload);
            }
            if (
              res2.payload.addressId != null &&
              res2.payload.addressId.length > 0
            ) {
              dispatch(getIdentityAddressDetailsAsync(res2.payload.addressId));
            }
            setIsSyncingBasic(false);
          });
        },
      );
    } else if (activeTab === 'Omistajat ja huoltajat') {
      updateAccounts();
    } else if (activeTab === 'Tiedotteet ja viestintä') {
      const notesupdate = getNotesUpdateData(accommodationBasicTemp);
      dispatch(updateAccommodationNotesAsync(notesupdate)).then(res => {
        dispatch(getAccommodationNotesAsync(accommodationDetailsBasic.id));
      });
    } else if (activeTab === 'Rakennukset, Huoneet ja tilat') {
      /* empty */
    } else if (activeTab === 'Kuvaukset') {
      const updateAccommodationdata = getUpdateData(accommodationBasicTemp);
      dispatch(updateAccommodatioBasicAsync(updateAccommodationdata)).then(
        res => {
          setIsSyncingBasic(true);
          dispatch(
            getAccommodationDetailsBasicAsync(params.accommodationId),
          ).then(res2 => {
            if (res2.meta.requestStatus === 'fulfilled') {
              setAccommodationBasic(res2.payload);
            }
            setIsSyncingBasic(false);
          });
        },
      );
    } else if (activeTab === 'Ominaisuudet / Varustelut') {
      const updateData = {
        id: accommodationBasicTemp.id,
        attributes: accommodationAttributesState.accommodationAttributes,
      } as AccommodationAttributesUpdate;
      dispatch(updateAccommodationsAttributesAsync(updateData)).then(res => {
        dispatch(getAccommodationAttributesAsync(accommodationDetailsBasic.id));
      });
      /* empty */
    } else if (activeTab === 'Lisäpalvelut') {
      /* empty */
    } else if (activeTab === 'Varaustilanne') {
      /* empty */
    } else if (activeTab === 'Kuvat') {
      /* empty */
    } else if (activeTab === 'Matkanjärjestäjä') {
      const updateAccommodationdata = getUpdateData(accommodationBasicTemp);
      dispatch(updateAccommodatioBasicAsync(updateAccommodationdata)).then(
        res => {
          dispatch(
            getAccommodationDetailsBasicAsync(params.accommodationId),
          ).then(res2 => {
            if (res2.meta.requestStatus === 'fulfilled') {
              setAccommodationBasic(res2.payload);
            }
          });
        },
      );
    }
    if (updateOk) {
      dispatch(addStatusToast({ Message: `Kohteen tiedot päivitetty.` }));
    }
  };
  const updateAccommodationAttributesOnClose = () => {
    updateAccommodation();
  };

  const saveAddon = () => {
    if (
      editedAddOn !== undefined &&
      editedAddOn.addOnDefinitionId !== undefined &&
      editedAddOn.id === null
    ) {
      // We have new addon, fill it to accommodation
      let tempaddons = accommodationAddOnsState.addOns;
      tempaddons = [...tempaddons, editedAddOn];
      const storedAccommodationAddonData = {
        ...accommodationAddOnsState,
        addOns: tempaddons,
      };
      setAccommodationAddOnsState(storedAccommodationAddonData);
      const updateAccommodationAddOndata = {
        id: accommodationDetailsBasic.id,
        addOns: storedAccommodationAddonData.addOns,
      } as AccommodationAddOnsUpdate;
      dispatch(
        updateAccommodationsAddOnsAsync(updateAccommodationAddOndata),
      ).then(res => {
        dispatch(
          getAccommodationAddOnsAccountsAsync(accommodationBasicTemp.id),
        );
        dispatch(getAccommodationAddOnsAsync(accommodationDetailsBasic.id));
      });
    } else {
      let tempaddons = accommodationAddOnsState.addOns;
      tempaddons = updateAddons(tempaddons);
      const storedAccommodationData = {
        ...accommodationAddOnsState,
        addOns: tempaddons,
      };
      setAccommodationAddOnsState(storedAccommodationData);
      const updateAccommodationAddOndata = {
        id: accommodationDetailsBasic.id,
        addOns: storedAccommodationData.addOns,
      } as AccommodationAddOnsUpdate;
      dispatch(
        updateAccommodationsAddOnsAsync(updateAccommodationAddOndata),
      ).then(res => {
        dispatch(getAccommodationAddOnsAsync(accommodationDetailsBasic.id));
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Majoitukset - Kohteen tiedot</title>
      </Helmet>

      <Container className="container-content">
        {accommodationDetailsLoadingStatus !== LOADING_STATUS.FAILED && (
          <SectionHeader
            BackLink={() => navigate(-1)}
            Breadcrumbs={['Majoitukset ja Ryhmät', 'Majoitukset']}
            Title={`Kohteen tiedot: ${
              (accommodationBasicTemp &&
                accommodationBasicTemp.accommodationNumber) !== undefined
                ? accommodationBasicTemp.accommodationNumber
                : ''
            } ${getItem('fi_FI', 'AccommodationName', accommodationBasicTemp) ?? ''}`}
            Children={
              <div>
                {accommodationBasicTemp.isAllowedToEdit && (
                  <Button
                    onClick={() => setShowCopyModal(true)}
                    variant="outline-blue"
                    className="me-8"
                  >
                    Kopioi kohde
                  </Button>
                )}
                {accommodationBasicTemp.isAllowedToEdit &&
                  activeTab !== 'iCal' && (
                    <Button onClick={updateAccommodation} variant="blue">
                      Tallenna kohteen tiedot
                    </Button>
                  )}
                {!accommodationBasicTemp.isAllowedToEdit && (
                  <Alert variant="primary" className="mt-16">
                    Huom!, Et voi muokata toisen varaamon kohdetta.
                  </Alert>
                )}
              </div>
            }
          />
        )}
        {accommodationDetailsLoadingStatus !== LOADING_STATUS.FAILED &&
          accommodationDetailsBasic && (
            <div className="mb-24">
              <Link
                to={`/rates/seasonrates/${accommodationDetailsBasic.id}?accommodation=${accommodationBasicTemp.accommodationNumber} - ${getItem('fi_FI', 'AccommodationName', accommodationBasicTemp) ?? ''}`}
              >
                Siirry kohteen hintakausiin
              </Link>
              <br />
              <Link
                to={`/rates/blockedperiods/${accommodationDetailsBasic.id}`}
              >
                Siirry kohteen estettyihin jaksoihin
              </Link>
            </div>
          )}
        <Loader
          IsLoading={
            accommodationDetailsLoadingStatus === LOADING_STATUS.LOADING ||
            isSyncingBasic
          }
        />
        {accommodationDetailsLoadingStatus !== LOADING_STATUS.FAILED && (
          <VerticalTabs
            ActiveTab={activeTab}
            ChangeTab={tab => intiateTabAndfech(tab)}
            TabContainerId="accommodationTabs"
            Tabs={Tabs}
          >
            <VerticalTabPane
              key={0}
              IsActive={activeTab === Tabs[0]}
              Name={Tabs[0]}
            >
              <Suspense fallback={<div>Ladataan...</div>}>
                <AccommodationForm
                  accommodationBasicTemp={accommodationBasicTemp}
                  addressDetailsTemp={addressDetailsTemp}
                  addressLoadingStatus={addressLoadingStatus}
                  countryOptions={countryOptions}
                  regionOptions={regionOptions}
                  municipalityOptions={municipalityOptions}
                  skiResortOptions={skiResortOptions}
                  placeOptions={placeOptions}
                  accommodationGroupsList={accommodationGroupsList}
                  handleInputChange={handleInputChange}
                  handleInputChangeTranslation={handleInputChangeTranslation}
                  handleInputChangeGeoCode={handleInputChangeGeoCode}
                  handleSelectChangeGroup={handleSelectChangeGroup}
                  handleSelectChangeAddress={handleSelectChangeAddress}
                  handleSelectChangeAccommodationLocation={
                    handleSelectChangeAccommodationLocation
                  }
                  handleCoordinatesSelect={handleCoordinatesSelect}
                  handleTagSelect={handleTagSelect}
                  handleInputChangeAddress={handleInputChangeAddress}
                  accommodationDetailsLoadingStatus={
                    accommodationDetailsLoadingStatus
                  }
                  updateAccommodation={updateAccommodation}
                  updateAccommodationRemoveCrossSell={
                    updateAccommodationRemoveCrossell
                  }
                  capacityIdsTextAreaRef={capacityIdsTextAreaRef}
                />
              </Suspense>
            </VerticalTabPane>
            <VerticalTabPane
              key={1}
              IsActive={activeTab === Tabs[1]}
              Name={Tabs[1]}
            >
              <Loader
                IsLoading={
                  originalAccommodationAccountListLoading ===
                  LOADING_STATUS.LOADING
                }
              />
              {originalAccommodationAccountListLoading ===
                LOADING_STATUS.IDLE && (
                <div className="my-48">
                  <div className="d-flex align-items-start mt-48">
                    <h2 className="h5 h-md-4 flex-grow-1 me-16 mb-24">
                      Omistajat ja huoltajat
                      <OverlayTrigger
                        placement="top" // 'top' | 'bottom' | 'left' | 'right' | 'auto' variations
                        overlay={
                          <Tooltip id="danger-tip" style={{ color: 'red' }}>
                            Siirtää omistajan omat ja väliaikaiset varaukset
                            omistajalle jolla on indeksi 0
                          </Tooltip>
                        }
                      >
                        <Button
                          size="sm"
                          className="ms-16"
                          variant="danger"
                          disabled={!accommodationBasicTemp.isAllowedToEdit}
                          onClick={() => changeOldOwnerBookingsToNewOwner()}
                        >
                          Siirrä omat varaukset uudelle omistajalle
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top" // 'top' | 'bottom' | 'left' | 'right' | 'auto' variations
                        overlay={
                          <Tooltip id="danger-tip" style={{ color: 'red' }}>
                            Siirtää uudelle huoltajalle(indeksiltään pienin)
                            voimassa olevat varaukset.
                          </Tooltip>
                        }
                      >
                        <Button
                          size="sm"
                          className="ms-16"
                          variant="danger"
                          disabled={!accommodationBasicTemp.isAllowedToEdit}
                          onClick={() =>
                            changeMaintainerBookingsToNewMaintainer()
                          }
                        >
                          Siirrä varaukset uudelle huoltajalle
                        </Button>
                      </OverlayTrigger>
                    </h2>
                    <Button
                      size="sm"
                      disabled={!accommodationBasicTemp.isAllowedToEdit}
                      onClick={() => openAddModal()}
                    >
                      <Icon Name="plus" CssClass="me-8" />
                      Lisää uusi
                    </Button>
                  </div>
                  <div className="table-responsive-lg mb-48">
                    <Table>
                      <thead>
                        <tr key="accountSelectHeader">
                          <th>Numero</th>
                          <th>Nimi</th>
                          <th>Rooli</th>
                          <th>Omistaja</th>
                          <th />
                          <th />
                        </tr>
                      </thead>
                      <tbody>{accountsSelectOptions}</tbody>
                    </Table>
                  </div>
                </div>
              )}
            </VerticalTabPane>
            <VerticalTabPane
              key={2}
              IsActive={activeTab === Tabs[2]}
              Name={Tabs[2]}
            >
              <div className="my-48">
                <AccommodationInputText
                  HandleChange={handleInputChangeTranslation}
                  Disbled={!accommodationBasicTemp.isAllowedToEdit}
                  InputId="AccommodationInternalInformationFiFi"
                  InputValue={getItem(
                    'fi_FI',
                    'AccommodationInternalInformation',
                    accommodationBasicTemp,
                  )}
                  IsTextarea
                  InputClassName="form-control-textarea-320-640-expand"
                  LabelText="Sisäistä tietoa ja viestintää"
                  InputName="AccommodationInternalInformation"
                />
              </div>

              <div className="d-flex align-items-start mb-16">
                <h2 className="h5 flex-grow-1 me-16">
                  Notes (Alla olevat tiedot välittyvät rajapintoja pitkin)
                </h2>
                <Button
                  size="sm"
                  disabled={!accommodationBasicTemp.isAllowedToEdit}
                  onClick={() => addInternalNote()}
                >
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää Note
                </Button>
              </div>

              <div className="table-responsive-lg mb-48">
                <Table>
                  <thead>
                    <tr key="notesHeader1">
                      <th>Huomio</th>
                      <th style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>{accommodationNotesItems}</tbody>
                </Table>
              </div>
              <div className="d-flex align-items-start mb-16">
                <h2 className="h5 flex-grow-1 me-16">
                  Julkiset tiedotteet (näkyy verkkokaupassa)
                </h2>
                <Button
                  size="sm"
                  disabled={!accommodationBasicTemp.isAllowedToEdit}
                  onClick={() => addPublicNote()}
                >
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää tiedote
                </Button>
              </div>
              <div className="table-responsive-lg mb-48">
                <Table>
                  <thead>
                    <tr key="notesHeader2">
                      <th style={{ minWidth: 248, width: '60%' }}>Tiedote</th>
                      <th>Ajastus</th>
                      <th style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>{accommodationPublicNotesItems}</tbody>
                </Table>
              </div>
            </VerticalTabPane>
            <VerticalTabPane
              key={3}
              IsActive={activeTab === Tabs[3]}
              Name={Tabs[3]}
            >
              <div className="d-flex align-items-start mt-48 mb-24">
                <h2 className="h5 h-md-4 flex-grow-1 me-16">Rakennukset</h2>
                <Button
                  size="sm"
                  disabled={!accommodationBasicTemp.isAllowedToEdit}
                  onClick={handleShowSelectNew}
                >
                  <Icon Name="plus" CssClass="me-8" />
                  Lisää Rakennus
                </Button>
              </div>
              {buildingsLoadingStatus !== LOADING_STATUS.IDLE && <Spinner />}
              {buildingItems.length < 1 &&
                buildingsLoadingStatus === LOADING_STATUS.IDLE && (
                  <p>Ei rakennuksia.</p>
                )}
              {buildingItems.length > 0 &&
                buildingsLoadingStatus === LOADING_STATUS.IDLE && (
                  <div className="table-responsive-lg mb-40">
                    <Table>
                      <thead>
                        <tr key="notesHeader3">
                          <th colSpan={3}>Nimi</th>
                        </tr>
                      </thead>
                      <tbody>{buildingItems}</tbody>
                    </Table>
                  </div>
                )}
            </VerticalTabPane>
            <VerticalTabPane
              key={4}
              IsActive={activeTab === Tabs[4]}
              Name={Tabs[4]}
            >
              <Suspense fallback={<div>Ladataan...</div>}>
                <AccommodationDescriptions
                  accommodationBasicTemp={accommodationBasicTemp}
                  accommodationDetailsLoadingStatus={
                    accommodationDetailsLoadingStatus
                  }
                  handleInputChangeTranslation={handleInputChangeTranslation}
                  rewriteAccommodationDescription={
                    rewriteAccommodationDescription
                  }
                  translateAccommodationDescription={
                    translateAccommodationDescription
                  }
                  handleInputChangeTranslationStringV2={
                    handleInputChangeTranslationV2
                  }
                />
              </Suspense>
            </VerticalTabPane>
            <VerticalTabPane
              key={5}
              IsActive={activeTab === Tabs[5]}
              Name={Tabs[5]}
            >
              <Loader
                IsLoading={
                  accommodationAttributesLoadingStatus ===
                  LOADING_STATUS.LOADING
                }
              />
              {accommodationAttributesLoadingStatus === LOADING_STATUS.IDLE && (
                <Suspense fallback={<div>Ladataan...</div>}>
                  <Attributes
                    disabled={!accommodationBasicTemp.isAllowedToEdit}
                    handleShowSelectAttribute={handleShowSelectAttribute}
                    accommodationVarusteluAttributeItems={
                      accommodationVarusteluAttributeItems
                    }
                    accommodationMokinTiedotAttributeItems={
                      accommodationMokinTiedotAttributeItems
                    }
                    accommodationOtherAttributeItems={
                      accommodationOtherAttributeItems
                    }
                    accommodationDistancesAttributeItems={
                      accommodationDistancesAttributeItems
                    }
                  />
                </Suspense>
              )}
            </VerticalTabPane>
            <VerticalTabPane
              key={6}
              IsActive={activeTab === Tabs[6]}
              Name={Tabs[6]}
            >
              <Loader
                key="loader6"
                IsLoading={
                  accommodationAddOnsLoadingStatus === LOADING_STATUS.LOADING
                }
              />
              {accommodationAddOnsLoadingStatus === LOADING_STATUS.IDLE && (
                <AddOnList
                  disabled={!accommodationBasicTemp.isAllowedToEdit}
                  handleShowSelectNewAddon={handleShowSelectNewAddon}
                  accommodationAddOnItems={accommodationAddOnItems}
                />
              )}
            </VerticalTabPane>
            <VerticalTabPane
              key={7}
              IsActive={activeTab === Tabs[7]}
              Name={Tabs[7]}
            >
              <Suspense fallback={<div>Ladataan...</div>}>
                <CalendarView
                  isCapacityProduct={
                    accommodationDetailsBasic
                      ? accommodationDetailsBasic.isCapacityProduct
                      : false
                  }
                  accommodationNumberConverted={
                    accommodationDetailsBasic
                      ? accommodationDetailsBasic.accommodationNumber
                      : 0
                  }
                  attributeResponse={
                    accommodationAttributesState.attributeResponse
                  }
                  otherResponse={accommodationAttributesState.otherResponse}
                  cottageResponse={accommodationAttributesState.cottageResponse}
                  distanceResponse={
                    accommodationAttributesState.distanceResponse
                  }
                  descriptionStart={getItem(
                    'fi_FI',
                    'AccommodationDescriptionStart',
                    accommodationBasicTemp,
                  )}
                  descriptionEnd={getItem(
                    'fi_FI',
                    'AccommodationDescriptionend',
                    accommodationBasicTemp,
                  )}
                  drivingInstructions={getItem(
                    'fi_FI',
                    'AccommodationDriveInstruction',
                    accommodationBasicTemp,
                  )}
                  hasSportumId={
                    !!(accommodationDetailsBasic?.sportumId || false)
                  }
                />
              </Suspense>
            </VerticalTabPane>

            <VerticalTabPane
              key={8}
              IsActive={activeTab === Tabs[8]}
              Name={Tabs[8]}
            >
              <Loader
                IsLoading={
                  accommodationImagesLoadingStatus === LOADING_STATUS.LOADING
                }
              />
              {accommodationImagesLoadingStatus === LOADING_STATUS.IDLE && (
                <ImagesView
                  accommodationBasicTemp={accommodationBasicTemp}
                  accommodationDetailsBasic={accommodationDetailsBasic}
                  transferring={transferring}
                  error={error}
                  errorText={errorText}
                  imagesList={imagesList}
                  transferImagesGrediAzureCdn={() =>
                    transferImagesGrediAzureCdn(
                      accommodationBasicTemp.accommodationNumber.toString(),
                    )
                  }
                  fixImageIndexes={() => fixImageIndexes()}
                  removeImage={removeImage}
                  HandleItemReorder={HandleItemReorder}
                  updateImages={updateImages}
                />
              )}
            </VerticalTabPane>
            <VerticalTabPane
              key={9}
              IsActive={activeTab === Tabs[9]}
              Name={Tabs[9]}
            >
              <Form>
                <Row xs={1} lg={1} className="mt-48">
                  <Col>
                    <Form.Label htmlFor="ownerId" className="form-label">
                      Majoituksen matkanjärjestäjä toimialue
                    </Form.Label>
                    <Select
                      inputId="TravelAgencyDomain"
                      name="TravelAgencyDomain"
                      placeholder="Valitse toimialue"
                      isClearable
                      options={travelAgencyDomainsList}
                      classNames={{
                        control: state =>
                          'form-control form-control-react-select mb-24',
                      }}
                      onChange={option =>
                        handleTravelAgencyDomainSelectChange(option)
                      }
                      value={travelAgencyDomainsList.filter(
                        option =>
                          option.value ===
                          (accommodationBasicTemp &&
                          accommodationBasicTemp.travelAgencyDomain
                            ? accommodationBasicTemp.travelAgencyDomain
                            : ''),
                      )}
                    />
                  </Col>
                  <Col>
                    <Form.Label htmlFor="ownerId" className="form-label">
                      Matkatoimistot alennuksilla
                    </Form.Label>
                    <Select
                      isMulti
                      inputId="TravelAgencyDomain"
                      name="TravelAgencyDomain"
                      placeholder="Valitse toimialue"
                      isClearable
                      options={travelAgencyAccountsList}
                      classNames={{
                        control: state =>
                          'form-control form-control-react-select mb-24',
                      }}
                      onChange={option =>
                        handleTravelAgencyDiscountSelectChange(option)
                      }
                      value={travelAgencyAccountsList.filter(
                        option =>
                          accommodationBasicTemp &&
                          accommodationBasicTemp.travelAgenciesWithDiscounts &&
                          accommodationBasicTemp.travelAgenciesWithDiscounts.includes(
                            option.value,
                          ),
                      )}
                    />
                  </Col>
                </Row>
              </Form>
            </VerticalTabPane>
            <VerticalTabPane
              key={10}
              IsActive={activeTab === Tabs[10]}
              Name={Tabs[10]}
            >
              {activeTab === Tabs[10] && (
                <Suspense fallback={<div>Ladataan...</div>}>
                  <IcalIntegrations
                    accommodationId={accommodationBasicTemp.id}
                    accommodationNumber={
                      accommodationBasicTemp.accommodationNumber
                    }
                  />
                </Suspense>
              )}
            </VerticalTabPane>
            <VerticalTabPane
              key={11}
              IsActive={activeTab === Tabs[11]}
              Name={Tabs[11]}
            >
              <Loader
                IsLoading={
                  accommodationLogsLoadingStatus === LOADING_STATUS.LOADING
                }
              />
              {accommodationLogsLoadingStatus === LOADING_STATUS.IDLE && (
                <>
                  <Card className="border shadow-none mt-48">
                    <div className="table-responsive-lg">
                      <Table className="table-data">
                        <thead>
                          <tr key="logsHeader1">
                            <th>Aika</th>
                            <th>Tekijä</th>
                            <th>Muutoksen kohde</th>
                            <th>Eventti</th>
                            <th>Vanhat arvot</th>
                            <th>Uudet arvot</th>
                          </tr>
                        </thead>
                        <tbody>{accommodationLogItems}</tbody>
                      </Table>
                    </div>
                  </Card>
                  {accommodationLogs?.data?.length > 0 ? (
                    <PaginationComponent
                      OnPageClick={handleLogsPageClick}
                      PaginationData={logsPagination}
                    />
                  ) : (
                    <p className="mt-16">Ei lokitietoja.</p>
                  )}
                </>
              )}
            </VerticalTabPane>
          </VerticalTabs>
        )}

        <div className="my-48" />
        <Suspense fallback={<div>Ladataan...</div>}>
          <NumericPlainEdit
            seteditedAttributeItemData={editedAttributeDataLocal}
            editedAttributeItemData={editedAttributeItemData}
            editedAttributeItemType={editedAttributeItemType}
            fillStoredata={() => fillStoredata()}
            getAttributeItem={() =>
              getAttributeItem(editedAttributeItemData.attributeId)
            }
            getAttributeItemValue={() =>
              getAttributeItemValue(
                editedAttributeItemData!.attributeId,
                editedAttributeItemData,
              )
            }
            setShowNumericPlain={setShowNumericPlain}
            accommodationbuildings={buildings}
            handleChangeValueInt={event => handleChangeValueInt(event)}
            show={showNumericPlain}
            OnClose={updateAccommodationAttributesOnClose}
            handleCancelNewAttribute={handleCancelNewAttribute}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <NumericMaxEdit
            seteditedAttributeItemData={editedAttributeDataLocal}
            editedAttributeItemData={editedAttributeItemData}
            editedAttributeItemType={editedAttributeItemType}
            fillStoredata={() => fillStoredata()}
            getAttributeItem={() =>
              getAttributeItem(editedAttributeItemData.attributeId)
            }
            getAttributeItemValue={() =>
              getAttributeItemValue(
                editedAttributeItemData!.attributeId,
                editedAttributeItemData,
              )
            }
            setShowNumericMax={setShowNumericMax}
            accommodationbuildings={buildings}
            handleChangeValueFloat={event => handleChangeValueFloat(event)}
            show={showNumericMax}
            OnClose={updateAccommodationAttributesOnClose}
            handleCancelNewAttribute={handleCancelNewAttribute}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <NumericMinEdit
            seteditedAttributeItemData={editedAttributeDataLocal}
            editedAttributeItemData={editedAttributeItemData}
            editedAttributeItemType={editedAttributeItemType}
            fillStoredata={() => fillStoredata()}
            getAttributeItem={() =>
              getAttributeItem(editedAttributeItemData.attributeId)
            }
            getAttributeItemValue={() =>
              getAttributeItemValue(
                editedAttributeItemData!.attributeId,
                editedAttributeItemData,
              )
            }
            setShowNumericMin={setShowNumericMin}
            accommodationbuildings={buildings}
            handleChangeValueFloat={event => handleChangeValueFloat(event)}
            show={showNumericMin}
            OnClose={updateAccommodationAttributesOnClose}
            handleCancelNewAttribute={handleCancelNewAttribute}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <BooleanEdit
            seteditedAttributeItemData={editedAttributeDataLocal}
            editedAttributeItemData={editedAttributeItemData}
            editedAttributeItemType={editedAttributeItemType}
            fillStoredata={() => fillStoredata()}
            getAttributeItem={() =>
              getAttributeItem(editedAttributeItemData.attributeId)
            }
            getAttributeItemValue={() =>
              getAttributeItemValue(
                editedAttributeItemData!.attributeId,
                editedAttributeItemData,
              )
            }
            setShowBoolean={setShowBoolean}
            accommodationbuildings={buildings}
            handleChangeValueBoolean={event => handleChangeValueBoolean(event)}
            show={showBoolean}
            OnClose={updateAccommodationAttributesOnClose}
            handleCancelNewAttribute={handleCancelNewAttribute}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <AddOnEdit
            editedAddOnItemData={editedAddOn}
            setShowAddOn={setShowEditAddOn}
            handleChangeValueAddOn={event => handleChangeValueAddOn(event)}
            show={showEditAddOn}
            setEditedAddOnData={seteditedAddOn}
            saveAddon={saveAddon}
            owners={allAccountsForModal}
            accommodationAccounts={accommodationAccountList}
            accommodationGroupsList={accommodationGroupsList}
            handleSelectChangeGroupAddOn={handleSelectChangeGroupAddOn}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <AddOnAddNewModal
            editedAddOnItemData={editedAddOn}
            fillStoredata={() => fillStoredata()}
            setShowAddOn={setShowAddAddOn}
            handleChangeValueAddOn={event => handleChangeValueAddOn(event)}
            show={showAddAddOn}
            setEditedAddOnData={seteditedAddOn}
            addOnsList={addOnsList}
            accommodationAddOnsList={accommodationAddOnList}
            saveAddon={saveAddon}
            owners={allAccountsForModal}
            ownersLoading={accountsLoading}
            accommodationAccounts={accommodationAccountList}
            allroles={allRoles}
            accommodationGroupsList={accommodationGroupsList}
            handleSelectChangeGroupAddOn={handleSelectChangeGroupAddOn}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <FreeTextEdit
            seteditedAttributeItemData={editedAttributeDataLocal}
            editedAttributeItemData={editedAttributeItemData}
            editedAttributeItemType={editedAttributeItemType}
            fillStoredata={() => fillStoredata()}
            getAttributeItem={() =>
              getAttributeItem(editedAttributeItemData.attributeId)
            }
            getAttributeItemValue={() =>
              getAttributeItemValue(
                editedAttributeItemData!.attributeId,
                editedAttributeItemData,
              )
            }
            setShowFreeText={setShowFreeText}
            accommodationbuildings={buildings}
            handleChangeValueFreeText={event =>
              handleChangeValueFreeText(event)
            }
            show={showFreeText}
            OnClose={updateAccommodationAttributesOnClose}
            handleCancelNewAttribute={handleCancelNewAttribute}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <MultiSelectEdit
            availablbeOptions={editedAttributeItemData.multiSelectOptions}
            seteditedAttributeItemData={editedAttributeDataLocal}
            editedAttributeItemData={editedAttributeItemData}
            editedAttributeItemType={editedAttributeItemType}
            fillStoredata={() => fillStoredata()}
            getAttributeItem={() =>
              getAttributeItem(editedAttributeItemData.attributeId)
            }
            getAttributeItemValue={() =>
              getAttributeItemValue(
                editedAttributeItemData!.attributeId,
                editedAttributeItemData,
              )
            }
            setShowMultiSelect={setShowMultiSelect}
            GetMultiSelectOptions={GetMultiSelectOptions}
            accommodationbuildings={buildings}
            handleChangeValueMultiSelect={handleChangeValueMultiSelect}
            show={showMultiSelect}
            OnClose={updateAccommodationAttributesOnClose}
            handleCancelNewAttribute={handleCancelNewAttribute}
          />
        </Suspense>
        <div>{GetSelectNewAttributeModal}</div>
        <Suspense fallback={<div>Ladataan...</div>}>
          <BuildingModal
            editedBuildingData={editedBuilding}
            show={showBuildingModal}
            setShowModal={setshowBuildingModal}
            allBuildings={allBuildings}
            allFloors={allFloors}
            allRoomAreas={allRoomAreas}
            allRooms={allRooms}
            allBeds={allBeds}
            updateBuildingRoomBed={updateBuildingRoomBed}
            updateBuildingRoom={updateBuildingRoom}
            updateBuildingRoomArea={updateBuildingRoomArea}
            refreshBuildingData={refreshBuildingData}
            setEditedBuildingData={setEditedBuilding}
            accommodationId={params.accommodationId}
          />
        </Suspense>
        <Suspense fallback={<div>Ladataan...</div>}>
          <AddNewBuildingModal
            setShowAddBuilding={setShowBuilding}
            saveNewBuilding={saveNewBuildingLocal}
            show={showBuilding}
            editedBuilding={editedNewBuilding}
            setEditedBuilding={setEditedNewBuilding}
            allBuildings={allBuildingOptions}
            handleSelectAddBuilding={handleSelectAddBuilding}
          />
        </Suspense>
        <CopyAccommodationModal
          show={showCopyModal}
          onHide={() => setShowCopyModal(false)}
          sourceAccommodationId={accommodationDetailsBasic.id}
          accommodationNumber={accommodationDetailsBasic.accommodationNumber}
        />
        <Modal show={showAttributesModal} fullscreen>
          <Modal.Header>
            <Modal.Title>Valitse varustelut</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-24 py-24 px-lg-40">
            <Suspense fallback={<div>Ladataan...</div>}>
              <AttributesAddAll
                disabled={!accommodationBasicTemp.isAllowedToEdit}
                handleShowSelectAttribute={handleShowSelectAttribute}
                handleCloseSelectAttribute={handleCloseSelectAttribute}
                accommodationAttributes={accommodationAttributes}
                accommodationAttributesLoadingStatus={
                  accommodationAttributesLoadingStatus
                }
                accommodationAttributesState={accommodationAttributesState}
                setAccommodationAttributesState={
                  setAccommodationAttributesState
                }
              />
            </Suspense>
          </Modal.Body>
        </Modal>
        <Modal show={isAddAccountModalVisible} backdrop="static">
          <Modal.Header>
            <Modal.Title>Lisää / Muokkaa omistaja</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-24">
              <Form.Label>Omistaja / Huoltaja</Form.Label>
              <InputGroup className="mb-3">
                <Form.Control
                  type="text"
                  onChange={handleInputChangeSelectFilter}
                  id="searchString"
                  name="searchString"
                  value={accountsFiltering.searchString}
                  placeholder="Hae omistajia"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={accountsSelectSearch}
                >
                  <span className="bi bi-search fs-6">
                    <span className="visually-hidden">Hae</span>
                  </span>
                </Button>
              </InputGroup>
              <Loader IsLoading={accountsLoading !== LOADING_STATUS.IDLE} />
              <Select
                inputId="accountId"
                name="accountId"
                placeholder="Valitse tili"
                isSearchable
                isDisabled={newAccountSelectDisabled}
                options={accountOptions}
                onChange={option =>
                  handleSelectChangeOwnerPick(option, 'accountId')
                }
                value={accommodationAccountOptions.filter(
                  option => option.value === editedAccount.id,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>
            <Form.Group className="mb-24">
              <Form.Label>Roolissa:?</Form.Label>
              <Select
                inputId="roleId"
                name="roleID"
                placeholder="Valitse rooli"
                isDisabled={
                  allowRoleSelect === false ||
                  !(accountRolesLoading === LOADING_STATUS.IDLE)
                }
                isSearchable
                options={userRolesOptionsOptions}
                value={userRolesOptionsOptions.filter(
                  option => option.value === editedAccount.roleId,
                )}
                onChange={option =>
                  handleSelectChangeRoleToPickedAccount(option, 'roleId')
                }
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>
            <Form.Group className="mb-24">
              <AccommodationInputNumber
                HandleChange={handleInputChangeAccountIndex}
                Disbled={!accommodationBasicTemp.isAllowedToEdit}
                InputId="index"
                InputName="index"
                InputValue={editedAccount.index ?? ''}
                IsRequired
                LabelText="Indeksi"
              />
            </Form.Group>
            <div className="small text-center text-danger">
              Muista tallentaa muutokset !
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="link" size="sm" onClick={OkClearAddedAccounts}>
              Peruuta ja sulje
            </Button>
            {errorText && errorText.length > 0 && (
              <div className="text-danger">{errorText}</div>
            )}
            <Button variant="outline-blue" size="sm" onClick={OkAddAccount}>
              Tallenna
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}
export default AccommodationDetailsView;
